/**
 * RecruiterRoomPage — authenticated, recruiter-facing Live Interview room.
 *
 * Recruiters are id-addressed (LiveInterviewSession.id), never token-
 * addressed: they have no candidate access_token to authenticate with, so
 * this page is reached at /live-interview/sessions/:sessionId/room behind
 * the normal RoleRoute gate, while the candidate's own room lives on the
 * public, token-addressed route.
 *
 * LAYOUT: a real VS Code-style DOCKING WORKSPACE, built on dockview-react —
 * see WorkspaceDockview for the panel/tab renderers. All five sections
 * (Video, Live Scoring, Interview, Main Notes, Chat) are equal dockview
 * panels: any one can be dragged onto or beside any other to swap places or
 * split the space, resized from any edge (not just its original two
 * neighbors), closed via the × on its tab, and reopened from the header's
 * launcher (the icon row next to "Reset Layout" — click an icon to add that
 * section back, exactly like toggling a panel in Teams/Slack). The default
 * arrangement (see buildDefaultDockLayout) is Video on the left, full height,
 * beside a 2x2 grid — Interview | Live Scoring on top, Main Notes | Chat
 * below — but every one of those positions is just where a fresh session
 * starts, not a fixed shape: dockview treats "all five side by side" or any
 * other arrangement as equally valid. The whole layout (positions, sizes,
 * which sections are open) persists to localStorage keyed by
 * DOCK_LAYOUT_STORAGE_KEY, so it survives a reload; "Reset Layout" clears
 * that and rebuilds the default. Video is a genuine, equal panel here too —
 * only its CONTROL BAR (mic/camera/share/leave/etc.) is pinned to the bottom
 * of the whole screen regardless of where the Video panel itself currently
 * sits — see MeetingStage's `pinnedControlBar` prop and its own "PINNED
 * CONTROL BAR" header comment.
 *
 * This replaced an earlier fixed SplitPane tree (Pane/SplitPane, itself a
 * replacement for a FIVE independent FLOATING, draggable-anywhere windows
 * design — FloatingPanel/useFloatingPanelLayout — that read as five separate
 * cards scattered over a canvas rather than one cohesive workspace). Before
 * that, an even earlier version used a fixed-column, tabbed side panel
 * (MeetingSidePanel — still used by the candidate room).
 *
 * Recording: this page never binds the recorder to the tiles it can see.
 * MeetingStage keeps an off-screen pool of <video> elements per participant
 * and hands them over through onRecorderTilesChange — see MeetingStage's
 * header for why coupling the recorder to the visible (paginated, screen-
 * share-swapping, camera-off-avatar) grid would silently corrupt the
 * recording.
 *
 * "Start Interview" lives HERE, and nowhere else: a session is created with
 * no questions at all, and the first question is generated at the moment the
 * recruiter actually starts the interview. (The job's Live Interview tab
 * used to pre-generate it, which left a "pending" question sitting in a room
 * nobody had joined.)
 *
 * VERBAL QUESTIONS: the candidate now answers these themselves — their
 * browser RECORDS the spoken answer and the server transcribes it with
 * Whisper, so a verbal question is scored on its content like any other.
 * This room contributes three things to that: a "Show Text Box" toggle that
 * flips the candidate to a typed box for the current question instead; a mic
 * reminder on reveal (see handleShowQuestion — that recording mixes in THIS
 * room's audio, so an interviewer on mute cuts their own question out of it);
 * and the "Move Forward" button, which has been demoted from "the way to
 * answer a verbal question" to a manual force-advance (see handleOk — it submits no
 * transcript, which is NOT score-neutral).
 *
 * LEAVE vs. END INTERVIEW — two separate actions, deliberately:
 *  - "Leave" navigates this recruiter out of the room. The session stays in
 *    progress and every other participant (other recruiter seats, the
 *    candidate) carries on; this seat can come straight back to the same URL.
 *  - "End Interview" completes the session for EVERYONE. It is irreversible,
 *    so it confirms first, and it is announced over the signaling socket
 *    (`end-interview` -> the server's `interview-ended` fan-out) so every
 *    other room tears down at the same moment instead of finding out on its
 *    next manual refresh.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { Bot } from 'lucide-react'
import {
  ArrowLeftIcon, EyeIcon, EyeSlashIcon, CheckCircleIcon, SparklesIcon,
  PlayIcon, ExclamationTriangleIcon, MicrophoneIcon, PencilSquareIcon,
  InformationCircleIcon, ChevronDownIcon, ChevronUpIcon, DocumentTextIcon,
  ArrowsPointingOutIcon, ShieldCheckIcon,
} from '@heroicons/react/24/outline'
import {
  DockviewReact, themeLightSpaced, type DockviewApi, type DockviewReadyEvent, type AddPanelPositionOptions,
} from 'dockview-react'
import 'dockview-react/dist/styles/dockview.css'

import LoadingSpinner from '@/components/common/LoadingSpinner'
import Tooltip from '@/components/common/Tooltip'
import { integrityEventLabel, isIntegrityFlag } from '@/utils/liveInterviewIntegrity'
import {
  getSessionSummary, getTurnCredentials, listQuestions, submitAnswer, generateNextQuestion,
  generateInterview, getNotes, saveNote, getChatMessages, markChatSeen, getScoringSummary,
  completeSession, controlAiInterviewer, getAiInterviewerState, postRecordingChunk, getSpeechToken, getTranscript, type LiveInterviewQuestion, type QuestionQuotaEntry,
  type LiveInterviewScoringSummary,
} from '@/services/liveInterviewApi'
import { useLiveInterviewRecorder, type RecorderTile } from '@/hooks/useLiveInterviewRecorder'
import { useLiveInterviewMeeting, peerLeftNotice } from '@/hooks/useLiveInterviewMeeting'
import type { LiveInterviewIceServer } from '@/hooks/useLiveInterviewCall'
import useLiveTranscription from '@/hooks/useLiveTranscription'
import LiveInterviewChatPanel, { type LiveInterviewChatMessage } from '@/components/liveInterview/LiveInterviewChatPanel'
import LiveInterviewTranscriptPanel, { type LiveTranscriptSegment, MAX_LIVE_TRANSCRIPT_SEGMENTS } from '@/components/liveInterview/LiveInterviewTranscriptPanel'
import MeetingStage from '@/components/liveInterview/MeetingStage'
import AiInterviewerControlPanel from '@/components/liveInterview/AiInterviewerControlPanel'
import {
  WorkspacePanelContent, WorkspacePanelTab, WorkspaceWatermark, type WorkspacePanelParams,
  PANE_ICONS, PANE_TITLES, PANE_ORDER, type WorkspacePaneId,
} from '@/components/liveInterview/WorkspaceDockview'

// The workspace's five content regions — Video, Interview (current question
// + generate-next, merged), Live Scoring, Main Notes, Chat — each is one
// dockview panel (icon + title + close button, via WorkspacePanelTab; see
// WorkspaceDockview and buildDefaultDockLayout below). `interview` merges
// what used to be two separate panels (Current Question, Generate Next
// Question) into one, since they're tightly related — what's being asked
// now, and picking/generating what's next. WorkspacePaneId/PANE_ICONS/
// PANE_TITLES/PANE_ORDER live in WorkspaceDockview (imported above) since
// WorkspacePanelTab needs them too, to look its own icon up by id.

// Persists dockview's layout; bump the version suffix when buildDefaultDockLayout changes shape.
// v5: added the Integrity panel.
const DOCK_LAYOUT_STORAGE_KEY = 'recruiter-room-dockview-layout-v5'
// The one component id every panel uses — the panel's actual on-screen
// content is generic (WorkspacePanelContent just renders whatever
// `params.content` it's given), so a single renderer covers all five
// sections; what differs per panel is pushed in via params, not via a
// different component per id.
const DOCK_CONTENT_COMPONENT = 'content'
// Module-scope + never recreated — see WorkspaceDockview's own header
// comment for why dockview-react's `components` prop needs a stable
// reference across renders.
const DOCK_COMPONENTS = { [DOCK_CONTENT_COMPONENT]: WorkspacePanelContent }

type WorkspacePanelContentMap = Record<WorkspacePaneId, React.ReactNode>
type WorkspacePanelBadgeMap = Partial<Record<WorkspacePaneId, React.ReactNode>>

function addWorkspacePanel(
  api: DockviewApi,
  id: WorkspacePaneId,
  content: React.ReactNode,
  badge: React.ReactNode | undefined,
  position?: AddPanelPositionOptions
) {
  return api.addPanel<WorkspacePanelParams>({
    id,
    component: DOCK_CONTENT_COMPONENT,
    title: PANE_TITLES[id],
    params: { content, badge },
    position,
  })
}

// Default: Video | Interview (own column) | [Live Scoring + Chat +
// Transcript tabbed on top, Main Notes below] (own column) - all 3 columns
// forced to equal width so nothing gets cramped, Live Scoring shown first.
function buildDefaultDockLayout(api: DockviewApi, content: WorkspacePanelContentMap, badges: WorkspacePanelBadgeMap) {
  const video = addWorkspacePanel(api, 'video', content.video, badges.video)
  const interview = addWorkspacePanel(api, 'interview', content.interview, badges.interview, { referencePanel: 'video', direction: 'right' })
  const liveScoring = addWorkspacePanel(api, 'live-scoring', content['live-scoring'], badges['live-scoring'], { referencePanel: 'interview', direction: 'right' })
  addWorkspacePanel(api, 'chat', content.chat, badges.chat, { referencePanel: 'live-scoring', direction: 'within' })
  addWorkspacePanel(api, 'transcript', content.transcript, badges.transcript, { referencePanel: 'live-scoring', direction: 'within' })
  addWorkspacePanel(api, 'integrity', content.integrity, badges.integrity, { referencePanel: 'live-scoring', direction: 'within' })
  addWorkspacePanel(api, 'main-notes', content['main-notes'], badges['main-notes'], { referencePanel: 'live-scoring', direction: 'below' })
  const columnWidth = Math.floor(api.width / 3)
  video.api.setSize({ width: columnWidth })
  interview.api.setSize({ width: columnWidth })
  liveScoring.api.setActive()
}

// Pixels of clearance reserved at the very bottom of the workspace (see the
// fixed dockview-wrapping div at the end of this component) so panel content
// (Chat's message-input row, Main Notes' textarea) never renders flush
// against the true viewport bottom edge — that pixel strip is where
// MeetingStage's AutoHidingPinnedControlBar keeps its own always-present,
// `pointer-events: auto` hover hotzone (see that component's h-1.5 hotzone
// div), and the two must never overlap or the hotzone silently eats clicks
// meant for whatever real content sits underneath it. Deliberately a couple
// px taller than that hotzone (6px) rather than an exact match, so a
// sub-pixel rounding difference between the two independent measurements
// (this inline style vs. that component's Tailwind class) can never let them
// touch.
const BOTTOM_RESERVE_PX = 8

const NOTE_AUTOSAVE_DELAY_MS = 800
const RECORDING_CHUNK_INTERVAL_MS = 10000
// Joins skill_name + question_type_id into a single <select> option value —
// a control character neither a skill name nor a UUID would ever contain.
const OPTION_KEY_SEPARATOR = ''

function flattenQuota(quota: Record<string, QuestionQuotaEntry[]> | undefined) {
  const out: { skillName: string; entry: QuestionQuotaEntry }[] = []
  Object.entries(quota || {}).forEach(([skillName, entries]) => {
    ;(entries || []).forEach((entry) => out.push({ skillName, entry }))
  })
  return out
}

// theta is an IRT ability estimate (irt_engine.py), clamped to [-4, 4] and
// centered on 0 — a plain number means nothing to a recruiter mid-interview,
// so it's bucketed into the same rough tiers the target-difficulty logic
// already reasons in.
function abilityLabel(theta: number): string {
  if (theta < -2) return 'Poor'
  if (theta < -0.5) return 'Beginner'
  if (theta < 1) return 'Intermediate'
  return 'Advanced'
}

// AI mode "End Interview": how long to wait for the candidate's room to
// submit its in-progress answer and complete before completing directly.
const AI_END_WAIT_MS = 15000

export default function RecruiterRoomPage() {
  const { sessionId } = useParams<{ sessionId: string }>()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [quota, setQuota] = useState<Record<string, QuestionQuotaEntry[]>>({})
  // AI-interviewer mode: this room shows AiInterviewerControlPanel so the
  // recruiter can monitor the AI's decisions and start / advance / end.
  const [isAiSession, setIsAiSession] = useState(false)
  const [questions, setQuestions] = useState<LiveInterviewQuestion[]>([])
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null)
  const [mediaError, setMediaError] = useState(false)
  const [iceServers, setIceServers] = useState<LiveInterviewIceServer[] | null>(null)
  // Server-configured signaling-reconnect budget, read from the session
  // summary this page already loads on mount (the recruiter-side twin of the
  // candidate room's public session GET). Never hardcoded in the bundle.
  const [reconnectTimeoutMs, setReconnectTimeoutMs] = useState(60000)
  // Same pattern — server-configured live-transcript scrollback cap (see
  // MAX_LIVE_TRANSCRIPT_SEGMENTS's own docstring for the default used until
  // this loads).
  const [maxLiveTranscriptSegments, setMaxLiveTranscriptSegments] = useState(MAX_LIVE_TRANSCRIPT_SEGMENTS)
  const [questionShown, setQuestionShown] = useState(false)
  // Recruiter override for the CURRENT verbal question, off by default: the
  // candidate normally answers a verbal question by SPEAKING, captured live in
  // their own browser (see LiveInterviewRoomPage's verbal-capture block).
  // Toggling this on gives them a plain typed box for this question instead —
  // for a candidate who would rather type, or whose speech recognition is
  // doing a poor job. Per-question, so it resets whenever a new question is
  // generated: an override chosen for one question must not silently carry
  // into the next.
  const [verbalTextBoxEnabled, setVerbalTextBoxEnabled] = useState(false)
  // When on, a question generates itself the moment an answer lands (either
  // side — the recruiter's own "OK", or the candidate's `answer-submitted`
  // signal) using the same suggested-type heuristic the dropdown below
  // pre-hints, instead of waiting for the recruiter to pick a type and click
  // "Generate Next Question". A ref shadows the state so the WS message
  // handler (defined once, well before this can change) always reads the
  // CURRENT toggle value rather than the one captured at mount.
  const [autoGenerateEnabled, setAutoGenerateEnabled] = useState(true)
  const autoGenerateRef = useRef(autoGenerateEnabled)
  useEffect(() => { autoGenerateRef.current = autoGenerateEnabled }, [autoGenerateEnabled])
  // When on, a freshly generated question (first question, manual "Generate
  // Next Question", or an auto-generated one) is revealed to the candidate
  // immediately — skipping the separate "Display Question to Candidate"
  // click. Independent of autoGenerateEnabled: either toggle can be on
  // without the other.
  const [autoDisplayEnabled, setAutoDisplayEnabled] = useState(true)
  const autoDisplayRef = useRef(autoDisplayEnabled)
  useEffect(() => { autoDisplayRef.current = autoDisplayEnabled }, [autoDisplayEnabled])
  // Indirection for the same reason autoGenerateRef/maybeAutoGenerate need
  // one: revealQuestionToCandidate (defined later, once resolveModeFor/
  // verbalTextBoxEnabled/meeting exist) is called from generateNextQuestionOfType
  // and handleStartInterview, both defined earlier in this component — a
  // direct reference would close over a stale first-render version instead
  // of picking up the latest state each time it actually fires.
  const revealQuestionRef = useRef<(question: LiveInterviewQuestion, freshQuota?: Record<string, any>) => void>(() => {})
  // Live Scoring's per-skill breakdown collapses by default — only the
  // always-useful ability estimate/suggested-type summary stays visible
  // without an extra click.
  const [scoringExpanded, setScoringExpanded] = useState(false)
  // Per-question notes are off by default; the Notes button (next to Move
  // Forward) reveals them. Reset per question so a note box left open on one
  // question doesn't silently look like it belongs to the next.
  const [notesOpen, setNotesOpen] = useState(false)
  const [unreadChat, setUnreadChat] = useState(0)
  // Integrity events received live from the candidate during this call (see
  // the 'integrity-event' app message), shown in the Integrity panel. Since
  // this recruiter joined - the full list, including anything earlier, is
  // on the scorecard. `unseenIntegrity` works like unreadChat: counts events
  // that arrived while the Integrity tab wasn't the active one.
  const [integrityAlerts, setIntegrityAlerts] = useState<Array<{ eventType: string; at: string }>>([])
  const [unseenIntegrity, setUnseenIntegrity] = useState(0)
  // The live dockview instance, set once by handleDockReady (below) and read
  // imperatively everywhere else in this component — a ref, not state,
  // because the WS message handler (set up once, well before this can
  // change) and several plain event handlers need the CURRENT api without
  // re-running on every dockview-internal change.
  const dockviewApiRef = useRef<DockviewApi | null>(null)
  // Disposables for the two dockview event subscriptions made in
  // handleDockReady — cleaned up on unmount (dockview itself is disposed by
  // DockviewReact; these are just OUR listeners on it).
  const dockDisposablesRef = useRef<{ dispose: () => void }[]>([])
  // Mirrors this render's actual pane content/badges for handlers that were
  // created once (empty/stable deps) and read them later, well after this
  // render has passed — see handleOpenPanel and the "Latest ref" comment by
  // paneContentRef's own assignment further down.
  const paneContentRef = useRef<WorkspacePanelContentMap>({} as WorkspacePanelContentMap)
  const paneBadgesRef = useRef<WorkspacePanelBadgeMap>({})
  // Which sections are currently open — kept as React state (mirrored from
  // api.panels on every dockview layout change, see handleDockReady) purely
  // so the header launcher below can render each icon's open/closed state
  // and this page can derive `chatOpen` for MeetingStage's chat toggle.
  const [openPanelIds, setOpenPanelIds] = useState<Set<WorkspacePaneId>>(() => new Set())
  // Bumped once dockview's onReady has actually run, so the content-sync
  // effect below (which needs dockviewApiRef.current to be non-null) fires
  // at least once right after the dock is ready, instead of only on the
  // next unrelated state change.
  const [dockReady, setDockReady] = useState(false)

  // Pushes the latest pane content/icon/badge (paneContentRef/paneBadgesRef,
  // kept current by an assignment further down in this render) into every
  // dockview panel that currently exists. MUST be declared here, before this
  // component's early-return guards below (loading/loadError/interviewEnded)
  // — every hook in a component has to run in the same order on every
  // render, early return or not, so a `useEffect` declared after those
  // guards would silently vanish from a loading/error render and break that
  // rule. Reading through the refs (rather than closing over `paneContent`/
  // `paneBadges` directly) is what makes that possible: those two are only
  // computed much later, past the guards, but the refs already exist here.
  // Runs after every commit (no dependency array) rather than once —
  // dockview panels are entirely outside React's tree, so this is the only
  // way their content ever picks up new state; the cost is negligible (five
  // cheap prop updates), and it correctly no-ops via the `!api` guard on
  // every loading/error/ended render, where dockview isn't even mounted.
  useEffect(() => {
    const api = dockviewApiRef.current
    if (!api || !dockReady) return
    PANE_ORDER.forEach((id) => {
      api.getPanel(id)?.api.updateParameters({
        content: paneContentRef.current[id],
        badge: paneBadgesRef.current[id],
      })
    })
  })

  // Cleans up handleDockReady's two event subscriptions on unmount — same
  // "must run before the early-return guards" reasoning as the effect above.
  useEffect(() => {
    return () => {
      dockDisposablesRef.current.forEach((d) => d.dispose())
      dockDisposablesRef.current = []
    }
  }, [])

  // This room is a fixed h-[100dvh] workspace — nothing on it is meant to
  // ever hand a scrollbar to the BROWSER WINDOW itself; every section that
  // needs to scroll does so internally (its own overflow-y-auto). But
  // dockview lays its grid out in absolute pixels computed from a
  // ResizeObserver read of its own container, and on some renders (a group
  // acquiring a wide tab strip, a font not yet done loading, a sub-pixel
  // rounding step) that measurement can land a hair wider/taller than the
  // viewport for a moment — enough for the DOCUMENT to become scrollable
  // even though every element in the actual React tree already carries
  // overflow-hidden. Belt-and-suspenders: force html/body non-scrollable for
  // as long as this room is mounted, restoring whatever they were on
  // unmount, so this page can never hand the rest of the app a scrollbar it
  // didn't ask for either. This alone still leaves a first-paint gap (a
  // useEffect only runs AFTER the initial paint), which is why the render
  // below ALSO ships an `html, body { overflow: hidden !important }`
  // stylesheet rule (see that <style> tag's own comment) — that one is live
  // from this component's very first frame, this one is what restores the
  // exact prior value on unmount.
  useEffect(() => {
    const { documentElement, body } = document
    const prevHtmlOverflow = documentElement.style.overflow
    const prevBodyOverflow = body.style.overflow
    documentElement.style.overflow = 'hidden'
    body.style.overflow = 'hidden'
    return () => {
      documentElement.style.overflow = prevHtmlOverflow
      body.style.overflow = prevBodyOverflow
    }
  }, [])

  const [otherTyping, setOtherTyping] = useState(false)
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [submittingOk, setSubmittingOk] = useState(false)
  // Always set together (see the dropdown's composite option value below) —
  // skill_name is required alongside question_type_id because the type id is
  // a shared, org-wide catalog id reused across skills (see
  // generateNextQuestion's own comment).
  const [selectedSkillName, setSelectedSkillName] = useState('')
  const [selectedTypeId, setSelectedTypeId] = useState('')
  const [generating, setGenerating] = useState(false)
  const [starting, setStarting] = useState(false)
  const [ending, setEnding] = useState(false)
  const [scoring, setScoring] = useState<LiveInterviewScoringSummary | null>(null)
  const [mainNote, setMainNote] = useState('')
  const [questionNotes, setQuestionNotes] = useState<Record<string, string>>({})
  const [chatMessages, setChatMessages] = useState<LiveInterviewChatMessage[]>([])
  const [transcriptSegments, setTranscriptSegments] = useState<LiveTranscriptSegment[]>([])
  // Set when ANY participant's "End Interview" completes the session — our
  // own, or another recruiter seat's, relayed as `interview-ended`.
  const [interviewEnded, setInterviewEnded] = useState(false)

  const authToken = useMemo(() => {
    try {
      return localStorage.getItem('ai_recruit_token')
    } catch {
      return null
    }
  }, [])
  const noteSaveTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({})
  // "Move Forward" (handleOk) asks the candidate's browser to submit whatever
  // they currently have entered (see the 'force-submit' app message below)
  // rather than immediately force-advancing with a blank placeholder. This
  // holds the pending promise's resolver while waiting for their
  // `answer-submitted` reply, keyed to the specific question so a stale
  // reply (or one for a DIFFERENT question, e.g. the candidate already moved
  // on) never resolves the wrong wait.
  const pendingForceSubmitRef = useRef<{ questionId: string; resolve: (captured: boolean) => void } | null>(null)

  const unmountedRef = useRef(false)
  const mediaStreamRef = useRef<MediaStream | null>(null)
  const recorder = useLiveInterviewRecorder()
  const recorderStartedRef = useRef(false)
  const latestRecorderTilesRef = useRef<RecorderTile[]>([])
  // Guards generateNextQuestionOfType against overlapping calls — e.g. the
  // candidate's `answer-submitted` broadcast and the recruiter's own "Move
  // Forward" (handleOk) landing close together both call into auto-generate/
  // this function, and neither knows about the other. Without this, a second
  // call can pass its OWN quota pre-check using data fetched before the
  // first call's generation actually committed, then get rejected by the
  // backend once it does — surfacing as a confusing "No remaining quota"
  // error for what was really just a duplicate, redundant attempt. A ref
  // (not the `generating` state) because this must be read synchronously at
  // the top of the very next call, not after a render.
  const generatingRef = useRef(false)

  const currentQuestion = questions.length > 0 ? questions[questions.length - 1] : null

  useEffect(() => { setNotesOpen(false) }, [currentQuestion?.id])

  useEffect(() => {
    if (!sessionId) return
    Promise.all([getSessionSummary(sessionId), listQuestions(sessionId), getTurnCredentials()])
      .then(([summary, qs, ice]) => {
        if (unmountedRef.current) return
        setQuota(summary.question_quota || {})
        // AI-mode sessions run entirely over ai_interviewer.py's own channel,
        // which this room never listens to - without this, a recruiter who
        // opens the room (nothing stops "Join Now" on one) would just see a
        // silently idle workspace with no explanation.
        if (summary.interview_mode === 'ai') {
          setIsAiSession(true)
          toast(
            'This interview is being conducted by the AI Interviewer. Use the AI Interviewer control panel to monitor its decisions, or start, advance or end the interview yourself.',
            { icon: '🤖', duration: 8000 },
          )
        }
        setQuestions(qs)
        setIceServers(ice)
        setReconnectTimeoutMs(Math.max(0, summary.reconnect_timeout_seconds ?? 60) * 1000)
        setMaxLiveTranscriptSegments(summary.max_live_transcript_segments || MAX_LIVE_TRANSCRIPT_SEGMENTS)
        setLoading(false)
      })
      .catch(() => {
        if (unmountedRef.current) return
        setLoadError('Could not load this interview session.')
        setLoading(false)
      })
  }, [sessionId])

  useEffect(() => {
    if (loading || loadError) return
    if (!navigator.mediaDevices?.getUserMedia) {
      setMediaError(true)
      return
    }
    navigator.mediaDevices
      // Echo cancellation requested explicitly — see LiveInterviewRoomPage's
      // matching call site for why a bare `audio: true` is not enough.
      .getUserMedia({
        video: true,
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      })
      .then((stream) => {
        if (unmountedRef.current) {
          stream.getTracks().forEach((t) => t.stop())
          return
        }
        mediaStreamRef.current?.getTracks().forEach((t) => t.stop())
        mediaStreamRef.current = stream
        setMediaStream(stream)
      })
      .catch(() => { if (!unmountedRef.current) setMediaError(true) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, loadError])

  // The scoring summary is the ONE read that runs on load, on an interval,
  // AND after every answer (whoever submitted it) — so it, not the handful of
  // write responses, is what keeps the quota panel honest.
  //
  // `quota` used to be written from exactly three places: the initial
  // getSessionSummary, and the generateInterview / generateNextQuestion
  // responses. Nothing refreshed it afterwards, so a second recruiter seat
  // (or a reload, or this tab sitting open while another tab generated a
  // question) kept rendering a snapshot that no longer matched the DB — the
  // "Remaining Quota" list and the next-question dropdown would claim quota
  // that was already spent, or show "No quota remaining" when there was
  // plenty. The summary endpoint already returns the session's live
  // question_quota every time it is called; syncing from it here makes every
  // refresh path (load, poll, candidate answer, own OK) converge on DB truth.
  const refreshScoring = useCallback(() => {
    if (!sessionId) return
    getScoringSummary(sessionId)
      .then((summary) => {
        setScoring(summary)
        if (summary.question_quota) setQuota(summary.question_quota)
      })
      .catch(() => {})
  }, [sessionId])

  // Re-reads the question list from the REST endpoint this page already
  // loads on mount. Used when the CANDIDATE submits an answer: that POST
  // goes to the public, token-gated endpoint and never touches this room's
  // state, so without a re-fetch the question it answered stays rendered
  // here as "pending" until someone reloads the page. Deliberately a plain
  // re-fetch of what we already know how to load — the answer/score itself
  // is never sent over the wire.
  const refreshQuestions = useCallback(() => {
    if (!sessionId) return
    listQuestions(sessionId)
      .then((qs) => setQuestions(qs))
      .catch(() => {})
  }, [sessionId])

  // Shared by the manual "Generate Next Question" button (handleGenerateNext,
  // below) and maybeAutoGenerate — the only difference between the two paths
  // is which (skill, type) pair they pass in. Both are required: type ids
  // come from a shared, org-wide question-type catalog reused across every
  // skill that includes it, so the type id ALONE can't tell the backend
  // which skill's quota to check/decrement (see generateNextQuestion's own
  // comment) — passing only a type id previously let it silently resolve to
  // a DIFFERENT skill that happened to share the same type and had already
  // run out, surfacing as a confusing "No remaining quota" error even when
  // the skill actually selected still had some left.
  const generateNextQuestionOfType = useCallback(async (skillName: string, typeId: string) => {
    if (!sessionId || !skillName || !typeId) return
    // See generatingRef's own comment — a second call arriving while one is
    // already in flight (auto-generate racing the recruiter's own action, or
    // vice versa) silently no-ops rather than duplicating/racing the request.
    if (generatingRef.current) return
    generatingRef.current = true
    setGenerating(true)
    try {
      const result = await generateNextQuestion(sessionId, skillName, typeId)
      setQuestions((prev) => [...prev, result.question])
      setQuota(result.question_quota)
      setQuestionShown(false)
      // The text-box override is per-question — a new question starts back at
      // the default (live speech capture) rather than inheriting whatever was
      // chosen for the previous one.
      setVerbalTextBoxEnabled(false)
      setSelectedSkillName('')
      setSelectedTypeId('')
      toast.success('Next question generated')
      if (autoDisplayRef.current) revealQuestionRef.current(result.question, result.question_quota)
      refreshScoring()
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Failed to generate the next question')
    } finally {
      generatingRef.current = false
      setGenerating(false)
    }
  }, [sessionId, refreshScoring])

  // Called from the two places an answer actually lands — the recruiter's
  // own "OK" and the candidate's `answer-submitted` signal — in place of a
  // plain refreshScoring() call there. Always re-fetches the scoring summary
  // (same data refreshScoring() would have fetched); when the toggle is on,
  // it then generates the next question itself using the freshly-returned
  // suggested_next_question_type_id, skipping silently if that type turns
  // out to have no quota left. Deliberately NOT wired into refreshScoring()
  // itself, which generateNextQuestionOfType also calls on success — folding
  // auto-generate in there would have it immediately try to chain into
  // another auto-generate after every generated question.
  const maybeAutoGenerate = useCallback(async () => {
    if (!sessionId) return
    // A generation is already in flight (this call's own doing, or the
    // recruiter's own "Move Forward" landing around the same time) — that
    // call's refreshScoring() will bring quota/scoring up to date once it
    // finishes, so this run has nothing useful left to do.
    if (generatingRef.current) return
    try {
      const summary = await getScoringSummary(sessionId)
      setScoring(summary)
      if (summary.question_quota) setQuota(summary.question_quota)
      if (!autoGenerateRef.current) return
      const typeId = summary.suggested_next_question_type_id
      const skillName = summary.suggested_skill_name
      if (!typeId || !skillName) return
      // Scoped to the SUGGESTED skill specifically (not "any skill with this
      // type"), matching how the backend now validates/decrements — a type
      // id shared by another skill that happens to still have quota is not
      // what was suggested and must not be silently substituted in.
      const hasQuota = ((summary.question_quota || {})[skillName] || []).some(
        (e) => e.question_type_id === typeId && e.remaining > 0
      )
      if (!hasQuota) return
      await generateNextQuestionOfType(skillName, typeId)
    } catch {
      // Best-effort — the manual "Generate Next Question" button is still there.
    }
  }, [sessionId, generateNextQuestionOfType])

  const meeting = useLiveInterviewMeeting({
    sessionId: sessionId || null,
    localStream: mediaStream,
    iceServers,
    enabled: !loading && !loadError && !interviewEnded && !!mediaStream && !!iceServers,
    role: 'recruiter',
    authToken,
    reconnectTimeoutMs,
    onAppMessage: (message) => {
      if (message?.type === 'chat' && message.id) {
        setChatMessages((prev) => (prev.some((m) => m.id === message.id) ? prev : [...prev, message as LiveInterviewChatMessage]))
        // Only the candidate's own messages count as unread here (this is
        // the recruiter's room) — and only while Chat isn't the panel the
        // recruiter is actually looking at right now (dockview's own active-
        // panel concept, read live off the ref rather than a stale closure).
        if (message.sender_role !== 'recruiter' && dockviewApiRef.current?.activePanel?.id !== 'chat') {
          setUnreadChat((n) => n + 1)
        }
        return
      }
      if (message?.type === 'transcript-segment' && message.id) {
        // Same dedupe-by-id guard as chat above — the sender's own emit
        // reaches here too (server broadcasts to the whole room, sender
        // included), so this is the single source of truth for the
        // combined, both-roles transcript, not just "the other side's".
        // Capped to the most recent maxLiveTranscriptSegments (server-
        // configured, see its own declaration) — the full history is never
        // lost, only this live in-call view's scrollback.
        setTranscriptSegments((prev) => {
          if (prev.some((s) => s.id === message.id)) return prev
          const next = [...prev, message as LiveTranscriptSegment]
          return next.length > maxLiveTranscriptSegments ? next.slice(-maxLiveTranscriptSegments) : next
        })
        return
      }
      if (message?.type === 'typing') {
        // The signaling relay never echoes a generic broadcast back to its
        // own sender, so any 'typing' message received here is always from
        // the candidate — no self-filtering needed.
        setOtherTyping(!!message.is_typing)
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)
        if (message.is_typing) {
          // Safety net — clears a stuck indicator if a final "stopped typing"
          // signal is ever lost (dropped packet, reconnect mid-keystroke).
          typingTimeoutRef.current = setTimeout(() => setOtherTyping(false), 5000)
        }
        return
      }
      if (message?.type === 'answer-submitted') {
        // The candidate answered whatever was on screen. Pull the question
        // list and scoring summary fresh so the question flips to
        // "answered" and the next-question controls are obviously live,
        // and drop the local "currently shown" flag since their reveal
        // panel has already cleared itself.
        setQuestionShown(false)
        refreshQuestions()
        maybeAutoGenerate()
        // Resolves handleOk's wait, if this is the reply to its own
        // 'force-submit' — see pendingForceSubmitRef above.
        if (pendingForceSubmitRef.current && pendingForceSubmitRef.current.questionId === message.question_id) {
          pendingForceSubmitRef.current.resolve(true)
          pendingForceSubmitRef.current = null
        }
        return
      }
      if (message?.type === 'integrity-event' && typeof message.event_type === 'string') {
        // Live integrity alert from the candidate's room (it's also stored
        // server-side and listed on the summary). Shown as a toast and kept
        // in the toolbar's Integrity list for the rest of this call.
        const eventType: string = message.event_type
        const at = typeof message.at === 'string' ? message.at : new Date().toISOString()
        setIntegrityAlerts((prev) => [...prev, { eventType, at }])
        if (isIntegrityFlag(eventType)) {
          toast(`Integrity: ${integrityEventLabel(eventType)}`, { icon: '⚠️', id: `integrity-${eventType}` })
          const dockApi = dockviewApiRef.current
          const integrityActive = !!dockApi && !!dockApi.activePanel && dockApi.activePanel.id === 'integrity'
          if (!integrityActive) setUnseenIntegrity((n) => n + 1)
        }
        return
      }
      if (message?.type === 'interview-ended') {
        // Another recruiter seat ended it — tear down here too rather than
        // leaving a live-looking room attached to a completed session.
        setInterviewEnded(true)
        return
      }
      if (message?.type === 'peer-left') {
        // Their tile is already gone (useLiveInterviewCall closed the peer
        // connection) — that always worked. What was missing was anyone being
        // TOLD. Suppressed for a reconnect-eviction, where the same person's
        // peer-joined is moments away; see peerLeftNotice.
        const notice = peerLeftNotice(message)
        if (notice) toast(notice, { icon: '👋' })
      }
    },
  })
  const { sendAppMessage } = meeting

  // Timestamp-Linked Feedback Annotations: live transcription. `enabled`
  // deliberately mirrors `meeting`'s own enabled condition (loaded, not
  // errored, not ended, mic ready) — this hook has no dependency of its own
  // beyond the mic stream and sendAppMessage, both already gated correctly
  // by that condition.
  const { transcriptionStatus, interimText } = useLiveTranscription({
    role: 'recruiter',
    mediaStream,
    enabled: !loading && !loadError && !interviewEnded && !!mediaStream,
    sendAppMessage,
    getToken: getSpeechToken,
  })

  // Phase D, Part 2: recruiter notes — one "main" note + one per question.
  useEffect(() => {
    if (!sessionId) return
    getNotes(sessionId)
      .then((notes) => {
        const perQuestion: Record<string, string> = {}
        let main = ''
        notes.forEach((n) => {
          if (n.question_id) perQuestion[n.question_id] = n.text
          else main = n.text
        })
        setMainNote(main)
        setQuestionNotes(perQuestion)
      })
      .catch(() => { /* notes are a convenience, not load-blocking */ })
  }, [sessionId])

  // Phase D, Part 3: chat history — the live WS `chat` broadcast (above)
  // covers everything sent from here on; this is just the backlog.
  useEffect(() => {
    if (!sessionId) return
    getChatMessages(sessionId)
      .then((history) => setChatMessages((prev) => {
        const existingIds = new Set(prev.map((m) => m.id))
        return [...history.filter((m) => !existingIds.has(m.id)), ...prev]
      }))
      .catch(() => { /* chat history is a convenience, not load-blocking */ })
  }, [sessionId])

  // Timestamp-Linked Feedback Annotations: transcript history — same
  // backlog-only pattern as chat above. Without this, a recruiter who
  // reloads mid-interview (or a second seat joining partway through) would
  // see the live transcript panel start empty, even though everything said
  // before they (re)joined is already persisted — the live WS
  // `transcript-segment` broadcast (above) only ever covers what's said
  // FROM this mount onward.
  useEffect(() => {
    if (!sessionId) return
    getTranscript(sessionId)
      .then((history) => setTranscriptSegments((prev) => {
        const existingIds = new Set(prev.map((s) => s.id))
        const merged = [...history.filter((s) => !existingIds.has(s.id)), ...prev]
        // Same live-scrollback cap as the WS-append path above — a long
        // interview's full history can be much larger than what the live
        // view should hold; only the most recent lines are kept here.
        return merged.length > maxLiveTranscriptSegments ? merged.slice(-maxLiveTranscriptSegments) : merged
      }))
      .catch(() => { /* transcript history is a convenience, not load-blocking */ })
  }, [sessionId, maxLiveTranscriptSegments])

  // One-time load only — no background poll. Scoring/quota otherwise stay
  // current via the event-triggered refreshes that already cover every real
  // "moved to the next question" moment: generateNextQuestionOfType's own
  // refreshScoring() on success, and maybeAutoGenerate's fetch from the
  // recruiter's own "Move Forward" or the candidate's answer-submitted signal.
  useEffect(() => {
    if (loading || loadError || !sessionId || interviewEnded) return
    refreshScoring()
  }, [loading, loadError, sessionId, interviewEnded, refreshScoring])

  const handleSendChat = (text: string) => {
    sendAppMessage({ type: 'chat', text })
  }

  const handleTypingChange = (isTyping: boolean) => {
    sendAppMessage({ type: 'typing', is_typing: isTyping })
  }

  const handleMarkChatSeen = useCallback(
    (messageId: string) => (sessionId ? markChatSeen(sessionId, messageId) : Promise.resolve()),
    [sessionId]
  )

  const scheduleNoteSave = (key: string, questionId: string | null, text: string) => {
    if (!sessionId) return
    if (noteSaveTimers.current[key]) clearTimeout(noteSaveTimers.current[key])
    noteSaveTimers.current[key] = setTimeout(() => {
      saveNote(sessionId, questionId, text).catch(() => toast.error('Failed to save note'))
    }, NOTE_AUTOSAVE_DELAY_MS)
  }

  const handleMainNoteChange = (text: string) => {
    setMainNote(text)
    scheduleNoteSave('main', null, text)
  }

  const handleQuestionNoteChange = (questionId: string, text: string) => {
    setQuestionNotes((prev) => ({ ...prev, [questionId]: text }))
    scheduleNoteSave(questionId, questionId, text)
  }

  useEffect(() => {
    return () => {
      Object.values(noteSaveTimers.current).forEach((t) => clearTimeout(t))
    }
  }, [])

  // --- Recording -------------------------------------------------------
  // Recruiter-controlled, not automatic — matching VMP's "Start Recording"
  // control (recruiter clicks it, everyone gets a broadcast so the candidate
  // knows recording is on). Previously this started the instant camera/mic
  // came up, with no way to turn it off and no signal to the candidate that
  // anything was even being recorded.
  const [isRecording, setIsRecording] = useState(false)

  // MeetingStage owns the off-screen <video> pool the recorder composites
  // from (see its header) and pushes the current list here. The latest list
  // is mirrored into a ref so the recorder can be started with whatever is
  // already mounted, regardless of which of the two lands first.
  const handleRecorderTiles = useCallback((tiles: RecorderTile[]) => {
    latestRecorderTilesRef.current = tiles
    if (recorderStartedRef.current) recorder.updateTiles(tiles)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const startRecording = useCallback(() => {
    if (!sessionId || !mediaStream || recorderStartedRef.current) return
    recorderStartedRef.current = true
    recorder.start(
      (index, chunk) => postRecordingChunk(sessionId, index, chunk),
      latestRecorderTilesRef.current,
      RECORDING_CHUNK_INTERVAL_MS
    )
    setIsRecording(true)
    sendAppMessage({ type: 'recording-state', is_recording: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, mediaStream])

  // Awaits the recorder's final chunk actually landing server-side (see
  // useLiveInterviewRecorder's stop()) — callers that need the recording
  // fully persisted before moving on (handleEndInterview) should await this
  // rather than fire-and-forgetting it, since the backend hard-rejects any
  // chunk uploaded after the session flips to "completed".
  const stopRecording = useCallback(async () => {
    if (!recorderStartedRef.current) return
    recorderStartedRef.current = false
    setIsRecording(false)
    sendAppMessage({ type: 'recording-state', is_recording: false })
    await recorder.stop()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const toggleRecording = useCallback(() => {
    if (isRecording) void stopRecording()
    else startRecording()
  }, [isRecording, startRecording, stopRecording])

  useEffect(() => {
    return () => {
      unmountedRef.current = true
      recorder.stop()
      mediaStreamRef.current?.getTracks().forEach((t) => t.stop())
      mediaStreamRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Another seat ended the interview. Tear this room down immediately rather
  // than waiting for a navigation: stop the recorder, release the camera/mic,
  // and drop the local stream — which also flips the meeting hook's `enabled`
  // false, closing the signaling socket and every peer connection.
  useEffect(() => {
    if (!interviewEnded) return
    recorder.stop()
    mediaStreamRef.current?.getTracks().forEach((t) => t.stop())
    mediaStreamRef.current = null
    setMediaStream(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interviewEnded])

  const flatQuota = useMemo(() => flattenQuota(quota), [quota])
  const availableTypes = flatQuota.filter((q) => q.entry.remaining > 0)

  // Opens (or, if already open, focuses) a section — shared by the header
  // launcher's icons and MeetingStage's own chat-toggle button (which every
  // room page must supply an onToggleChat for). Reopening attaches the
  // panel as a new tab alongside whatever group is currently active, so it
  // never fights the user's own arrangement; if every section has been
  // closed there's no active group left, and it's added as the sole panel,
  // filling the whole workspace.
  const handleOpenPanel = useCallback((id: WorkspacePaneId) => {
    const api = dockviewApiRef.current
    if (!api) return
    const existing = api.getPanel(id)
    if (existing) {
      existing.api.setActive()
      return
    }
    const active = api.activeGroup
    addWorkspacePanel(api, id, paneContentRef.current[id], paneBadgesRef.current[id], active ? { referenceGroup: active, direction: 'within' } : undefined)
  }, [])

  // Closes a section if open, opens it otherwise — the header launcher's
  // click behavior (an icon toggles its section exactly like Teams/Slack's
  // own sidebar icons: click to show, click again to hide — the × on the
  // tab itself does the same close).
  const handleTogglePanel = useCallback((id: WorkspacePaneId) => {
    const api = dockviewApiRef.current
    if (!api) return
    const existing = api.getPanel(id)
    if (existing) existing.api.close()
    else handleOpenPanel(id)
  }, [handleOpenPanel])

  const chatOpen = openPanelIds.has('chat')
  const handleToggleChatPane = useCallback(() => handleOpenPanel('chat'), [handleOpenPanel])

  // Resolves the response_mode/category the candidate room needs to render
  // the right reveal UI (code editor / whiteboard / textarea). The question
  // itself now carries both, resolved server-side from the question_types
  // catalog — that is authoritative. The quota snapshot is only a fallback:
  // with auto-display on, a reveal fires before setQuota has re-rendered, so
  // `flatQuota` here can be stale (or missing the type entirely), which used
  // to send a coding/design question to the candidate as a plain text box.
  // `freshQuota` (the quota from the same response) avoids that stale read.
  const resolveModeFor = (question: LiveInterviewQuestion, freshQuota?: Record<string, any>) => {
    if (question.response_mode || question.category) {
      return { response_mode: question.response_mode, category: question.category }
    }
    const source = freshQuota ? flattenQuota(freshQuota) : flatQuota
    const match = source.find((q) => q.entry.question_type_id === question.question_type_id)
    return { response_mode: match ? match.entry.response_mode : undefined, category: match ? match.entry.category : undefined }
  }

  // Whether the question on screen is a SPOKEN one. Resolved the same way the
  // candidate room resolves its answer surface — response_mode first, falling
  // back to category — because the question row itself carries neither (only
  // question_type_id); both live on this session's quota snapshot. This gates
  // the "Show Text Box" control, which is meaningless for a question the
  // candidate already answers in a box of some kind.
  // True once the current question has been answered and nothing new has
  // replaced it yet (auto-generate off, or its request just hasn't landed).
  // The current-question and generate-next-question sections are permanent
  // fixed sections of the Interview pane (see interviewScrollContent below)
  // rather than cards that swap places, so this just drives a small
  // "waiting for the next question" hint instead of hiding anything.
  const answeredAndIdle = !!currentQuestion && currentQuestion.status === 'answered'

  const currentQuestionIsVerbal = useMemo(() => {
    if (!currentQuestion) return false
    const { response_mode, category } = resolveModeFor(currentQuestion)
    return (response_mode || category) === 'verbal'
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestion, flatQuota])

  // Takes an explicit question rather than reading currentQuestion from the
  // closure, so a freshly generated question can be revealed the instant it
  // exists (see generateNextQuestionOfType/handleStartInterview's
  // autoDisplayRef calls) without waiting a render for currentQuestion to
  // catch up.
  const revealQuestionToCandidate = (question: LiveInterviewQuestion, freshQuota?: Record<string, any>) => {
    const { response_mode, category } = resolveModeFor(question, freshQuota)
    sendAppMessage({
      type: 'show-question',
      question: {
        id: question.id,
        question_text: question.question_text,
        skill_name: question.skill_name,
        question_type_id: question.question_type_id,
        response_mode,
        category,
        test_cases: question.test_cases,
      },
      // Carried on the reveal itself so the candidate opens the RIGHT answer
      // surface first time. The candidate room resets its own override on
      // every show-question, so without this a question hidden and re-shown
      // while the override was on would come back as speech capture while
      // this room still showed the toggle as on — two rooms disagreeing about
      // the same question. This keeps the recruiter the single source of truth.
      verbal_text_box_enabled: verbalTextBoxEnabled,
    })
    setQuestionShown(true)

    // The candidate's browser records this answer by mixing their own mic with
    // every incoming participant stream — including this one — so an
    // interviewer on mute silently removes their own question and follow-ups
    // from the recording that gets transcribed and scored.
    //
    // This room can only act on ITS OWN microphone: the candidate's mic is on
    // another machine and their room auto-unmutes it there for exactly the same
    // reason. So: unmute here if muted, and otherwise just say why it matters.
    // Only for a spoken question with no text-box override — with the override
    // on, the candidate is typing and nothing is being recorded at all.
    if ((response_mode || category) === 'verbal' && !verbalTextBoxEnabled) {
      if (!meeting.micOn) {
        meeting.toggleMic()
        toast('Your microphone was turned on — the candidate’s answer recording captures both sides of the conversation.', { icon: '🎤' })
      } else {
        toast('Please keep your microphone on — the candidate’s answer recording captures both sides of the conversation.', { icon: '🎤' })
      }
    }
  }
  revealQuestionRef.current = revealQuestionToCandidate

  const handleShowQuestion = () => {
    if (!currentQuestion) return
    revealQuestionToCandidate(currentQuestion)
  }

  /** Flips the candidate between recording a spoken answer and a typed box for
   *  the question currently on screen. Takes effect immediately — the candidate
   *  does not have to wait for a new question to be revealed. */
  const handleToggleVerbalTextBox = () => {
    if (!currentQuestionIsVerbal) return
    const next = !verbalTextBoxEnabled
    setVerbalTextBoxEnabled(next)
    sendAppMessage({ type: 'verbal-text-box-state', enabled: next })
  }

  const handleHideQuestion = () => {
    sendAppMessage({ type: 'hide-question' })
    setQuestionShown(false)
  }

  // How long "Move Forward" waits for the candidate's browser to reply with
  // 'answer-submitted' before giving up and falling back to a blank
  // placeholder. Long enough to cover a whiteboard snapshot or a verbal
  // clip's base64 encoding + upload; short enough that the recruiter never
  // feels like the button did nothing.
  const FORCE_SUBMIT_TIMEOUT_MS = 4000

  // Resolves once the candidate's browser replies to a 'force-submit' for
  // this exact question (see pendingForceSubmitRef / the 'answer-submitted'
  // handler above), or `false` after the timeout — e.g. the candidate has
  // nothing capturable yet (a verbal question with no recording started), or
  // their connection is down.
  const waitForCandidateSubmit = (questionId: string, timeoutMs: number = FORCE_SUBMIT_TIMEOUT_MS): Promise<boolean> =>
    new Promise((resolve) => {
      pendingForceSubmitRef.current = { questionId, resolve }
      setTimeout(() => {
        if (pendingForceSubmitRef.current?.questionId === questionId) {
          pendingForceSubmitRef.current = null
          resolve(false)
        }
      }, timeoutMs)
    })

  // "Move Forward" — captures whatever the candidate currently has entered
  // (partial code, a whiteboard sketch in progress, typed text, or a verbal
  // recording already in flight) rather than discarding it. It asks the
  // candidate's OWN browser to run its own submit — the same one their
  // "Submit Answer" button calls — against ITS current state, since that
  // state (the code editor's contents, the whiteboard canvas, an in-progress
  // recording) only ever lives there, never synced to the recruiter live.
  //
  // Falls back to the original behavior — force-advance with modality="audio"
  // and no transcript — only when the candidate has nothing to submit (a
  // fresh question, or verbal with no recording yet) or doesn't respond in
  // time. That fallback is deliberately NOT a neutral no-op: an absent
  // transcript is scored against a placeholder, which scores near zero and
  // pulls the session's ability estimate DOWN — a genuine last resort, not a
  // routine way to move on.
  const handleOk = async () => {
    if (!currentQuestion) return
    setSubmittingOk(true)
    try {
      sendAppMessage({ type: 'force-submit', question_id: currentQuestion.id })
      const captured = await waitForCandidateSubmit(currentQuestion.id)
      if (!captured) {
        await submitAnswer(currentQuestion.id, { modality: 'audio', transcript: null })
      }
      // Clears the candidate's own view of this question too — otherwise it
      // keeps showing on their screen, already answered from the recruiter's
      // side, with no indication anything happened. Same signal the manual
      // "Hide Question" button already sends. (A captured force-submit
      // already clears their view itself — see handleSubmitAnswer — but
      // sending this regardless is harmless and covers the fallback path.)
      sendAppMessage({ type: 'hide-question' })
      setQuestionShown(false)
      toast.success(captured ? 'Captured the candidate’s in-progress answer.' : 'Recorded — question marked as asked.')
      refreshQuestions()
      maybeAutoGenerate()
    } catch {
      toast.error('Could not record this yet — please try again.')
    } finally {
      setSubmittingOk(false)
    }
  }

  const handleStartInterview = async () => {
    if (!sessionId) return
    setStarting(true)
    try {
      const result = await generateInterview(sessionId)
      setQuestions((prev) => (prev.length > 0 ? prev : [result.question]))
      setQuota(result.question_quota)
      setVerbalTextBoxEnabled(false)
      toast.success('Interview started')
      if (autoDisplayRef.current) revealQuestionRef.current(result.question, result.question_quota)
      refreshScoring()
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Failed to start the interview')
    } finally {
      setStarting(false)
    }
  }

  // "Leave" — this seat only. The session stays in progress, every other
  // participant carries on, and unmount releases this browser's camera/mic
  // and stops its recorder. Never completes the session; that is what "End
  // Interview" (below) is for, and the two are separate buttons precisely so
  // one can't be mistaken for the other.
  const handleLeave = () => {
    if (!window.confirm('Leave this interview? It will continue for everyone else, and you can rejoin from the same link.')) return
    navigate(-1)
  }

  // Teams-style forced leave: once useLiveInterviewCall's own reconnect
  // budget (LIVE_INTERVIEW_RECONNECT_TIMEOUT_SECONDS, server-configurable)
  // is genuinely exhausted — not a deliberate eviction, see
  // LiveInterviewConnectionState's own docstring — this seat is kicked out
  // rather than left sitting on a room that looks alive but will never
  // recover on its own. A short grace period lets the "Connection lost —
  // leaving…" text (above) actually be seen before navigating away; the
  // session itself is untouched (this only ever affects this one seat,
  // same as a manual "Leave" — every other participant carries on).
  useEffect(() => {
    if (meeting.connectionState !== 'failed') return
    toast.error('Connection lost. Leaving the interview…')
    const timer = setTimeout(() => navigate(-1), 4000)
    return () => clearTimeout(timer)
  }, [meeting.connectionState, navigate])

  // Guided tour — same mechanism as Proctored Interview's own and Live
  // Interview's candidate-side one (LiveInterviewRoomPage's LiveInterviewTour):
  // null = inactive, 0 = intro card, 1..RECRUITER_TOUR_STEPS.length = a
  // spotlighted step. Targets each dockview panel's own TAB HEADER
  // (WorkspaceDockview's WorkspacePanelTab, via its `data-tour="dock-tab-*"`)
  // rather than a panel's content — Interview and Live Scoring are tabbed
  // together in the default layout (buildDefaultDockLayout's `direction:
  // 'within'`), so only one of their CONTENT areas is actually visible at a
  // time, but every group's tab strip always renders all of its tabs
  // regardless of which one is active, making tab headers the only anchors
  // that are reliably present the moment the tour starts.
  const [tourStep, setTourStep] = useState<number | null>(null)
  const tourStartedRef = useRef(false)
  useEffect(() => {
    if (!meeting.connected || tourStartedRef.current) return
    tourStartedRef.current = true
    setTourStep(0)
  }, [meeting.connected])

  const handleEndInterview = async () => {
    if (!sessionId) return
    if (!window.confirm('End this interview for everyone? This will finalize the score and cannot be undone.')) return
    setEnding(true)
    try {
      // Recording must actually stop (and its final chunk land) BEFORE the
      // session completes — the backend rejects any chunk uploaded once the
      // session's status is "completed", so a still-running recorder would
      // silently lose its last few seconds otherwise.
      if (isRecording) await stopRecording()
      if (isAiSession) {
        // AI mode: same "End Interview", but the candidate's room submits
        // the in-progress answer first (ai_interviewer end_interview), then
        // completes. Waits for that; completes directly as a fallback if
        // the candidate's room never applies it (e.g. they disconnected).
        await controlAiInterviewer(sessionId, 'end').catch(() => undefined)
        const deadline = Date.now() + AI_END_WAIT_MS
        let completed = false
        while (!completed && Date.now() < deadline) {
          await new Promise((r) => setTimeout(r, 1500))
          const st = await getAiInterviewerState(sessionId).catch(() => null)
          completed = st?.status === 'completed' || st?.status === 'aborted'
        }
        if (!completed) await completeSession(sessionId)
      } else {
        // Human mode: if the candidate has a question on screen they haven't
        // submitted yet, ask their browser to submit what they've entered
        // (the same 'force-submit' "Move Forward" uses) BEFORE completing —
        // otherwise their in-progress answer never counts toward the final
        // score. If they have nothing to submit or don't reply in time, the
        // question is simply left unanswered (it isn't scored as a zero).
        if (questionShown && currentQuestion && currentQuestion.status !== 'answered') {
          sendAppMessage({ type: 'force-submit', question_id: currentQuestion.id })
          // Longer than Move Forward's wait: a spoken answer is uploaded and
          // transcribed before the candidate's reply arrives, and anything
          // landing after completeSession wouldn't be in the final score.
          await waitForCandidateSubmit(currentQuestion.id, AI_END_WAIT_MS)
        }
        await completeSession(sessionId)
      }
      // Only announced AFTER the session is actually completed — telling
      // everyone to tear down and then failing to complete would leave the
      // room empty with the session still in progress. The server re-emits
      // this to the whole room (this connection included) as
      // `interview-ended`; other recruiter seats and the candidate handle it
      // in their own onAppMessage.
      sendAppMessage({ type: 'end-interview' })
      toast.success('Interview ended')
      navigate(`/live-interview/sessions/${sessionId}/summary`)
    } catch {
      toast.error('Failed to end the interview')
      setEnding(false)
    }
  }

  const handleGenerateNext = () => generateNextQuestionOfType(selectedSkillName, selectedTypeId)

  // A peer's REAL name whenever the server could resolve one (a direct
  // users.id lookup on the signaling connection — see
  // live_interview_signaling.py). Role words are only ever a fallback for a
  // participant who genuinely has no name to show: the candidate, who has no
  // user row at all, and an as-yet-unidentified peer.
  const remoteName = useCallback(
    (peerId: string, index: number) => {
      const state = meeting.peerStates[peerId]
      if (state?.displayName) return state.displayName
      if (state?.role === 'candidate') return 'Candidate'
      if (state?.role === 'recruiter') return 'Interviewer'
      return `Participant ${index + 1}`
    },
    [meeting.peerStates]
  )

  if (interviewEnded) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 max-w-sm w-full text-center space-y-4">
          <p className="text-gray-800 font-medium">This interview has ended.</p>
          <p className="text-sm text-gray-500">The session has been completed and scored.</p>
          <button
            onClick={() => navigate(`/live-interview/sessions/${sessionId}/summary`)}
            className="w-full px-3 py-2 bg-purple-600 text-white rounded-md text-sm font-medium hover:bg-purple-700"
          >
            View Scorecard
          </button>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (loadError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 max-w-sm w-full text-center">
          <p className="text-gray-700">{loadError}</p>
        </div>
      </div>
    )
  }

  // Content for the "Generate Next Question" section of the Interview pane
  // (see interviewScrollContent below) — just the inner controls, restyled
  // dark to match the workspace (see this page's visual-pass note above
  // interviewToggleRow).
  const generateNextQuestionSection = (
    <div className="space-y-2">
      {answeredAndIdle && availableTypes.length > 0 && (
        <p className="text-[11px] text-green-700">
          This question has been answered — pick the next question type below.
        </p>
      )}
      <select
        // The option's value carries BOTH skill_name and question_type_id
        // (joined by a separator no skill name or UUID would ever contain) —
        // a type id alone is a shared, org-wide catalog id that can repeat
        // across skills, so selecting just the id would be ambiguous about
        // which skill's quota to draw from (see generateNextQuestionOfType's
        // own comment).
        value={selectedSkillName && selectedTypeId ? `${selectedSkillName}${OPTION_KEY_SEPARATOR}${selectedTypeId}` : ''}
        onChange={(e) => {
          const [skillName, typeId] = e.target.value.split(OPTION_KEY_SEPARATOR)
          setSelectedSkillName(skillName || '')
          setSelectedTypeId(typeId || '')
        }}
        disabled={availableTypes.length === 0}
        className="w-full bg-white border border-gray-300 rounded-md px-2 py-1.5 text-xs text-gray-900 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 disabled:opacity-50 disabled:bg-gray-50"
      >
        <option value="">
          {availableTypes.length === 0 ? 'No quota remaining' : 'Select a question type…'}
        </option>
        {availableTypes.map(({ skillName, entry }) => (
          <option
            key={`${skillName}-${entry.question_type_id}`}
            value={`${skillName}${OPTION_KEY_SEPARATOR}${entry.question_type_id}`}
          >
            {skillName} — {entry.question_type_name || entry.question_type_id} ({entry.remaining} left)
          </option>
        ))}
      </select>
      <button
        onClick={handleGenerateNext}
        disabled={!selectedSkillName || !selectedTypeId || generating}
        className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white rounded-md text-xs font-medium hover:bg-purple-700 disabled:opacity-50"
      >
        {generating ? <LoadingSpinner size="sm" /> : <SparklesIcon className="w-3.5 h-3.5" />}
        {generating ? 'Generating…' : 'Generate Next Question'}
      </button>
    </div>
  )

  // Content for the Live Scoring pane — logic unchanged from the earlier
  // floating-panel version, restyled dark. Kept as one scrollable body (the
  // ability-estimate line is short and the per-skill list only appears once
  // expanded), consistent with "only the region that needs it scrolls".
  const liveScoringContent = scoring ? (
    <>
      {scoring.recommendation === 'consider_stopping' && (
        <div className="flex items-start gap-1.5 bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-2.5 py-1.5 text-xs mb-2">
          <ExclamationTriangleIcon className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <span>The last two answers scored low — consider wrapping up.</span>
        </div>
      )}
      <div className="flex items-center justify-between flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500 mb-1">
        <span>Ability estimate: <span className="font-medium text-gray-900">{abilityLabel(scoring.theta_estimate)}</span></span>
        {scoring.suggested_next_question_type_id && (
          <span className="text-[11px] text-gray-500">
            Suggested next type: <span className="font-medium text-gray-700">{
              flatQuota.find((q) => q.entry.question_type_id === scoring.suggested_next_question_type_id)?.entry.question_type_name
              || scoring.suggested_next_question_type_id
            }</span>
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={() => setScoringExpanded((v) => !v)}
        className="w-full flex items-center justify-between text-[11px] text-gray-500 hover:text-gray-700 pt-2 mt-2 border-t border-gray-200"
      >
        <span>Per-skill scores{scoring.questions.length > 0 ? ` (${scoring.questions.length})` : ''}</span>
        {scoringExpanded ? <ChevronUpIcon className="w-3.5 h-3.5" /> : <ChevronDownIcon className="w-3.5 h-3.5" />}
      </button>
      {scoringExpanded && (
        scoring.questions.length === 0 ? (
          <p className="text-xs text-gray-500 mt-1.5">No questions scored yet.</p>
        ) : (
          <ul className="space-y-1 mt-1.5">
            {scoring.questions.map((q) => (
              <li key={q.question_id} className="flex items-center gap-2 text-xs text-gray-600">
                <span className="truncate flex-1 min-w-0">#{q.sequence_number} {q.skill_name}</span>
                {/* Thin progress bar alongside the raw number — denser and
                    more scannable than the number alone across a list of
                    skills, per the redesign's "efficient use of width"
                    guidance. Rubric scores are 0-100. */}
                <span className="w-14 h-1 rounded-full bg-gray-200 overflow-hidden flex-shrink-0">
                  <span
                    className={`block h-full rounded-full ${q.rubric_score != null && q.rubric_score < 70 ? 'bg-amber-400' : 'bg-purple-500'}`}
                    style={{ width: `${Math.max(0, Math.min(100, q.rubric_score ?? 0))}%` }}
                  />
                </span>
                <span className={`font-medium w-7 text-right flex-shrink-0 ${q.rubric_score != null && q.rubric_score < 70 ? 'text-amber-600' : 'text-gray-900'}`}>
                  {q.rubric_score != null ? Math.round(q.rubric_score) : '—'}
                </span>
              </li>
            ))}
          </ul>
        )
      )}
    </>
  ) : (
    <p className="text-xs text-gray-500">Scoring will appear once the interview starts.</p>
  )

  // Content for the Interview pane's "current question" section — logic
  // unchanged, restyled dark.
  const currentQuestionContent = (
    <>
      {currentQuestion ? (
        <div className="bg-gray-50 border border-gray-200 rounded-md p-2.5">
          <div className="flex items-start justify-between gap-2">
            <p className="text-xs font-medium text-purple-700">{currentQuestion.skill_name}</p>
            {/* Flips to "Answered" as soon as an answer lands
                — the recruiter's own "OK", or the
                candidate's submission (which arrives as an
                `answer-submitted` signal that re-fetches
                this list). */}
            <span
              className={`flex-shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                currentQuestion.status === 'answered'
                  ? 'bg-green-50 text-green-700'
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              {currentQuestion.status === 'answered' ? 'Answered' : 'Pending'}
            </span>
          </div>
          <p className="text-sm text-gray-900 mt-1">{currentQuestion.question_text}</p>
          {/* This pane is a permanent fixed slot rather than
              one that gets replaced by Generate Next Question
              once answered — this hint carries the same
              "nothing to do here right now" intent without
              hiding the question that was just answered. */}
          {answeredAndIdle && (
            <p className="text-[11px] text-green-700 mt-2 pt-2 border-t border-gray-200">
              Answered — waiting for the next question.{' '}
              {availableTypes.length > 0
                ? 'Pick a type in Generate Next Question below'
                : 'No quota remains for a next question'}
              {autoGenerateEnabled ? ', or it will generate automatically.' : '.'}
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-gray-500">No question generated yet.</p>
          <button
            onClick={handleStartInterview}
            disabled={starting}
            className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white rounded-md text-xs font-medium hover:bg-purple-700 disabled:opacity-50"
          >
            {starting ? <LoadingSpinner size="sm" /> : <PlayIcon className="w-3.5 h-3.5" />}
            {starting ? 'Starting…' : 'Start Interview'}
          </button>
        </div>
      )}

      {/* Reveal + "Move Forward" disappear entirely once the
          question is answered — by WHOEVER answered it (this
          recruiter's own "Move Forward", or the candidate's
          own submission, which arrives as `answer-submitted`
          and re-fetches this list). Previously only the
          button was disabled while the row stayed on screen,
          which left "Display Question to Candidate"
          re-revealing a question that was already scored and
          done. */}
      {currentQuestion && currentQuestion.status !== 'answered' && (
        <div className="flex flex-wrap gap-2 mt-2">
          <button
            onClick={questionShown ? handleHideQuestion : handleShowQuestion}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50"
          >
            {questionShown ? <EyeSlashIcon className="w-3.5 h-3.5" /> : <EyeIcon className="w-3.5 h-3.5" />}
            {questionShown ? 'Hide from Candidate' : 'Display Question to Candidate'}
          </button>
          {/* Verbal questions only: everything else already
              gives the candidate a box of some kind, so
              there is nothing to override. Off by default —
              the candidate speaks and their browser captures
              it. */}
          {currentQuestionIsVerbal && (
            <button
              onClick={handleToggleVerbalTextBox}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border ${
                verbalTextBoxEnabled
                  ? 'bg-blue-600 border-blue-600 text-white hover:bg-blue-700'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
              title={
                verbalTextBoxEnabled
                  ? 'Switch the candidate back to answering out loud'
                  : 'Let the candidate type this answer instead of speaking it'
              }
            >
              {verbalTextBoxEnabled
                ? <MicrophoneIcon className="w-3.5 h-3.5" />
                : <PencilSquareIcon className="w-3.5 h-3.5" />}
              {verbalTextBoxEnabled ? 'Back to Speaking' : 'Show Text Box'}
            </button>
          )}
          <button
            onClick={handleOk}
            disabled={submittingOk}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-md text-xs font-medium hover:bg-green-700 disabled:opacity-50"
            title="Captures whatever the candidate currently has entered and moves on; scores as unanswered only if they have nothing yet"
          >
            {submittingOk ? <LoadingSpinner size="sm" /> : <CheckCircleIcon className="w-3.5 h-3.5" />}
            Move Forward
          </button>
          <button
            type="button"
            onClick={() => setNotesOpen((v) => !v)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border ${
              notesOpen ? 'bg-purple-50 border-purple-200 text-purple-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <DocumentTextIcon className="w-3.5 h-3.5" />
            Notes
          </button>
        </div>
      )}

      {/* Says, in the recruiter's own panel, what the
          candidate is currently seeing — otherwise "Show
          Text Box" is a toggle with no visible effect on
          this side of the call. */}
      {currentQuestionIsVerbal && currentQuestion && currentQuestion.status !== 'answered' && (
        <p className="text-[11px] text-gray-500 mt-1">
          {verbalTextBoxEnabled
            ? 'The candidate is typing this answer.'
            : 'The candidate answers out loud; the recording captures both sides and is transcribed when they submit.'}
        </p>
      )}

      {currentQuestion && notesOpen && (
        <div className="bg-gray-50 border border-gray-200 rounded-md p-2.5 mt-2">
          <p className="text-xs font-semibold text-gray-500 mb-1">Notes for this question</p>
          <textarea
            value={questionNotes[currentQuestion.id] || ''}
            onChange={(e) => handleQuestionNoteChange(currentQuestion.id, e.target.value)}
            placeholder="Private notes about this question…"
            className="w-full min-h-[60px] bg-white border border-gray-300 rounded-md px-2.5 py-1.5 text-xs text-gray-900 placeholder-gray-400 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 resize-y"
          />
        </div>
      )}
    </>
  )

  // Content for the Main Notes pane — a subtle, borderless "editor" surface
  // rather than a bordered input box, per the redesign's "no heavy
  // decorative border on the textarea itself" note.
  const mainNotesContent = (
    <textarea
      value={mainNote}
      onChange={(e) => handleMainNoteChange(e.target.value)}
      placeholder="Overall notes about this interview…"
      className="w-full h-full min-h-0 bg-transparent border-0 px-0 py-0 text-xs text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-0 resize-none"
    />
  )

  // The Interview pane's auto-generate/auto-display toggle row — deliberately
  // kept OUT of the pane's scrollable body (see interviewScrollContent
  // below): these two switches govern the whole pane's behavior and must
  // stay visible regardless of how long the current question or the
  // generate-next controls get, per "only the question/content area
  // scrolls, not the whole pane".
  const interviewToggleRow = (
    <div className="flex items-center justify-between flex-wrap gap-x-4 gap-y-1.5">
      <div className="flex items-center gap-1.5">
        <span className="text-[11px] font-medium text-gray-700">Auto-generate</span>
        <Tooltip
          content="Automatically generates the next question, using the suggested type, once an answer lands."
          position="bottom"
          theme="dark"
          width="18rem"
        >
          <InformationCircleIcon className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600 cursor-pointer" />
        </Tooltip>
        <button
          type="button"
          role="switch"
          aria-checked={autoGenerateEnabled}
          title="Auto-generate next question"
          onClick={() => setAutoGenerateEnabled((v) => !v)}
          className={`relative inline-flex flex-shrink-0 h-4 w-7 items-center rounded-full transition-colors ${
            autoGenerateEnabled ? 'bg-purple-600' : 'bg-gray-300'
          }`}
        >
          <span
            className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
              autoGenerateEnabled ? 'translate-x-3.5' : 'translate-x-0.5'
            }`}
          />
        </button>
      </div>

      <div className="flex items-center gap-1.5">
        <span className="text-[11px] font-medium text-gray-700">Auto-display</span>
        <Tooltip
          content="Auto-display: when on, a newly generated question is shown to the candidate immediately, without needing to click Display Question to Candidate."
          position="left"
          theme="dark"
          width="18rem"
        >
          <InformationCircleIcon className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600 cursor-pointer" />
        </Tooltip>
        <button
          type="button"
          role="switch"
          aria-checked={autoDisplayEnabled}
          title="Auto-display question to candidate"
          onClick={() => setAutoDisplayEnabled((v) => !v)}
          className={`relative inline-flex flex-shrink-0 h-4 w-7 items-center rounded-full transition-colors ${
            autoDisplayEnabled ? 'bg-purple-600' : 'bg-gray-300'
          }`}
        >
          <span
            className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
              autoDisplayEnabled ? 'translate-x-3.5' : 'translate-x-0.5'
            }`}
          />
        </button>
      </div>
    </div>
  )

  // The Interview pane's scrollable body — current question + generate-next
  // controls. Split out from interviewToggleRow (above) specifically so the
  // Interview pane's composition below can pin the toggle row and let only
  // THIS part scroll.
  const interviewScrollContent = (
    <>
      {currentQuestionContent}
      <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mt-3 pt-3 border-t border-gray-200">
        Generate Next Question
      </p>
      <div className="mt-1.5">{generateNextQuestionSection}</div>
    </>
  )

  // --- Pane bodies -------------------------------------------------------
  // Each is the full scrollable/padded body for one of the five dockview
  // panels below — pushed into that panel's params via the content-sync
  // effect further down, never rendered through dockview directly.
  const videoStage = (
    <MeetingStage
      className="h-full"
      localStream={mediaStream}
      // The signed-in recruiter's own name, resolved server-side on the
      // signaling connection; "You" only when there is genuinely nothing
      // to show (never a role label standing in for a name).
      localName={meeting.ownDisplayName || 'You'}
      screenStream={meeting.screenStream}
      remoteTiles={meeting.remoteTiles}
      remoteName={remoteName}
      peerStates={meeting.peerStates}
      presenterId={meeting.presenterId}
      micOn={meeting.micOn}
      cameraOn={meeting.cameraOn}
      sharing={meeting.sharing}
      screenShareSupported={meeting.screenShareSupported}
      onToggleMic={meeting.toggleMic}
      onToggleCamera={meeting.toggleCamera}
      onToggleScreenShare={meeting.toggleScreenShare}
      reactions={meeting.reactions}
      onSendReaction={meeting.sendReaction}
      // Chat is just another dockview panel now (see handleOpenPanel/
      // handleTogglePanel above) — "open" reads as "its panel currently
      // exists in the dock" (openPanelIds), and "toggle" reopens it
      // (attached beside whatever group is currently active) if closed.
      chatOpen={chatOpen}
      onToggleChat={handleToggleChatPane}
      chatUnreadCount={unreadChat}
      onLeave={handleLeave}
      leaveLabel="Leave"
      onEndInterview={handleEndInterview}
      endInterviewBusy={ending}
      networkQuality={meeting.networkQuality}
      connectionState={meeting.connectionState}
      mediaError={mediaError}
      onRecorderTilesChange={handleRecorderTiles}
      isRecording={isRecording}
      onToggleRecording={toggleRecording}
      // pinnedControlBar: restored (product requirement) — the mic/camera/
      // share/leave/recording bar must be a page-wide, auto-hiding bar
      // pinned to the bottom of the WHOLE screen, independent of wherever
      // the Video panel/dock group happens to be sized or positioned, not
      // trapped inside that panel's own box. This was previously left off
      // post-dockview-migration over a real but narrower concern: since
      // Video (and Chat) can now be dragged anywhere, including the bottom
      // row, a page-wide fixed bar COULD end up visually sitting on top of
      // whatever panel the user parked there, e.g. eating clicks meant for
      // Chat's own message input. That trade-off is inherent to "one
      // control bar, pinned to the screen, regardless of panel layout" —
      // it's not a bug introduced by dockview, and it's exactly what a
      // Zoom/Teams-style always-reachable control bar looks like: the same
      // trade-off existed, unremarked, in this page's pre-dockview layout.
      // Safe to re-enable independent of the page-scrollbar fix above:
      // AutoHidingPinnedControlBar portals straight to document.body, whose
      // only DOM ancestors are <html>/<body> — neither sets transform/
      // filter/perspective, so its `fixed` positioning is never hijacked by
      // any ancestor (dockview's own internal elements included) and it can
      // never itself introduce a page-level scrollbar (a `fixed` box doesn't
      // add to document flow/height). See MeetingStage's own "PINNED
      // CONTROL BAR" header comment for the full mechanism.
      pinnedControlBar
      // Forces the bar up out of its normal hover-to-reveal hiding during
      // its own tour step — otherwise it's translated below the viewport
      // and the spotlight would have nothing visible to highlight.
      forceControlBarVisible={
        tourStep !== null &&
        !!RECRUITER_TOUR_STEPS[tourStep - 1] &&
        RECRUITER_TOUR_STEPS[tourStep - 1].selector === '[data-tour="control-bar"]'
      }
    />
  )

  const scoringBody = (
    <div className="flex-1 min-h-0 overflow-y-auto li-scroll p-[var(--space-pad)]">{liveScoringContent}</div>
  )
  // Interview's toggle row is pinned (flex-shrink-0); only the question/
  // generate-next content below it scrolls — see interviewToggleRow's own
  // comment above for why the two are split out.
  const interviewBody = (
    <>
      <div className="flex-shrink-0 px-[var(--space-pad)] pt-[var(--space-pad)] pb-2 border-b border-gray-200">
        {interviewToggleRow}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto li-scroll p-[var(--space-pad)]">{interviewScrollContent}</div>
    </>
  )
  const notesBody = (
    <div className="flex-1 min-h-0 flex flex-col p-[var(--space-pad)]">{mainNotesContent}</div>
  )
  const chatBody = (
    <div className="flex-1 min-h-0 overflow-hidden p-[var(--space-pad)]">
      <LiveInterviewChatPanel
        className="h-full"
        ownRole="recruiter"
        messages={chatMessages}
        onSend={handleSendChat}
        markSeen={handleMarkChatSeen}
        otherTyping={otherTyping}
        onTypingChange={handleTypingChange}
        hideHeader
        fillHeight
      />
    </div>
  )

  const transcriptBody = (
    <div className="flex-1 min-h-0 overflow-hidden p-[var(--space-pad)]">
      <LiveInterviewTranscriptPanel
        className="h-full"
        ownRole="recruiter"
        segments={transcriptSegments}
        transcriptionStatus={transcriptionStatus}
        interimText={interimText}
        hideHeader
        fillHeight
      />
    </div>
  )

  // Shown in Chat's tab (via WorkspacePanelTab's `params.badge`) so it stays
  // visible even while Chat isn't the focused panel.
  const chatUnreadBadge = unreadChat > 0 ? (
    <span className="flex-shrink-0 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center">
      {unreadChat}
    </span>
  ) : null

  // "Latest ref" pattern (see handleOpenPanel above): handlers created once
  // (empty/stable deps) need a way to read this render's actual content
  // when they're eventually invoked, well after this render has passed —
  // mutating a ref during render is safe here since it never affects what
  // THIS render outputs, only what a later imperative call reads.
  // Integrity panel: the candidate's integrity events received live during
  // this call, newest first (see the 'integrity-event' app message). The full
  // list - including anything before this recruiter joined - is on the
  // scorecard.
  const integrityFlagCount = integrityAlerts.filter((a) => isIntegrityFlag(a.eventType)).length
  const integrityBody = (
    <div className="flex-1 min-h-0 flex flex-col p-[var(--space-pad)]">
      {integrityAlerts.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center gap-1.5 px-4">
          <ShieldCheckIcon className="w-6 h-6 text-green-500" />
          <p className="text-sm text-gray-600">No integrity events so far.</p>
          <p className="text-xs text-gray-400">
            Camera/mic detections, tab switches, blocked copy/paste and extra displays from the candidate show up here as they happen.
          </p>
        </div>
      ) : (
        <>
          <p className="pb-2 text-xs text-gray-500">
            <span className="font-semibold text-amber-600">{integrityFlagCount}</span> event{integrityFlagCount === 1 ? '' : 's'} since you joined
          </p>
          <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-1">
            {[...integrityAlerts].reverse().map((alert, i) => {
              const flag = isIntegrityFlag(alert.eventType)
              return (
                <div key={i} className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-gray-50 text-xs">
                  {flag
                    ? <ExclamationTriangleIcon className="w-3.5 h-3.5 flex-shrink-0 text-amber-500" />
                    : <CheckCircleIcon className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" />}
                  <span className="flex-1 text-gray-700">{integrityEventLabel(alert.eventType)}</span>
                  <span className="text-gray-400 tabular-nums">
                    {new Date(alert.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                  </span>
                </div>
              )
            })}
          </div>
          <p className="pt-2 text-[11px] text-gray-400">The full list is on the scorecard after the interview.</p>
        </>
      )}
    </div>
  )
  // Same count pill as Chat's unread badge, in amber.
  const integrityBadge = unseenIntegrity > 0 ? (
    <span className="flex-shrink-0 min-w-[16px] h-4 px-1 rounded-full bg-amber-500 text-white text-[10px] font-semibold flex items-center justify-center">
      {unseenIntegrity}
    </span>
  ) : null

  const paneContent: WorkspacePanelContentMap = {
    video: videoStage,
    interview: interviewBody,
    'live-scoring': scoringBody,
    'main-notes': notesBody,
    chat: chatBody,
    transcript: transcriptBody,
    integrity: integrityBody,
  }
  const paneBadges: WorkspacePanelBadgeMap = { chat: chatUnreadBadge, integrity: integrityBadge }
  paneContentRef.current = paneContent
  paneBadgesRef.current = paneBadges

  // Runs exactly once, on mount (see DockviewReact's own internal onReady
  // wiring — it fires a single time regardless of this prop's identity
  // changing on later renders) — so `paneContent`/`paneBadges` here are
  // this FIRST render's values, which is exactly right for
  // buildDefaultDockLayout: "No question generated yet.", "Scoring will
  // appear once the interview starts.", empty notes, no chat messages are
  // the correct initial placeholders. A restored (non-default) layout
  // doesn't depend on these at all — its real content arrives a moment
  // later from the sync effect above, once `dockReady` flips true.
  const handleDockReady = (event: DockviewReadyEvent) => {
    const api = event.api
    dockviewApiRef.current = api

    const layoutChangeDisposable = api.onDidLayoutChange(() => {
      // Skip the update when the actual set of open panel ids hasn't
      // changed — `new Set(...)` is always a fresh reference, which would
      // otherwise force a re-render on every firing of this event even when
      // nothing observable changed. That matters here specifically: any OTHER
      // frequent, unrelated re-render of this page (e.g. live transcription's
      // own status/interim-text state) can cause dockview to re-fire this
      // event on a tight cadence, and an unconditional setState here turns
      // that into a self-sustaining render loop rather than a bounded one.
      const nextIds = api.panels.map((p) => p.id) as WorkspacePaneId[]
      setOpenPanelIds((prev) => {
        if (prev.size === nextIds.length && nextIds.every((id) => prev.has(id))) return prev
        return new Set(nextIds)
      })
      try {
        // The replacer strips `content`/`badge` at every depth BEFORE
        // JSON.stringify ever recurses into them — panel params otherwise
        // hold live React elements (closures, event handlers), which are
        // unsafe/lossy to serialize and pointless to restore (real content
        // is always re-pushed by the sync effect above anyway).
        const serialized = JSON.stringify(api.toJSON(), (key, value) => (key === 'content' || key === 'badge' ? undefined : value))
        localStorage.setItem(DOCK_LAYOUT_STORAGE_KEY, serialized)
      } catch {
        // Persistence is a convenience, not load-bearing — a full/blocked
        // localStorage just means the layout resets next visit.
      }
    })
    const activePanelDisposable = api.onDidActivePanelChange(({ panel }) => {
      if (panel?.id === 'chat') setUnreadChat(0)
      if (panel && panel.id === 'integrity') setUnseenIntegrity(0)
    })
    dockDisposablesRef.current = [layoutChangeDisposable, activePanelDisposable]

    let restored = false
    try {
      const raw = localStorage.getItem(DOCK_LAYOUT_STORAGE_KEY)
      if (raw) {
        api.fromJSON(JSON.parse(raw))
        restored = true
      }
    } catch {
      restored = false
    }
    if (!restored) buildDefaultDockLayout(api, paneContent, paneBadges)
    setDockReady(true)
  }

  // "Reset Layout" — clears the persisted arrangement and rebuilds the
  // default from scratch (see buildDefaultDockLayout's own comment for the
  // exact shape). `api.clear()` removes every panel/group first so this
  // never leaves stray panels behind from whatever arrangement was on
  // screen.
  const handleResetDockLayout = () => {
    const api = dockviewApiRef.current
    if (!api) return
    try {
      localStorage.removeItem(DOCK_LAYOUT_STORAGE_KEY)
    } catch {
      // Best-effort — the in-memory reset below still takes effect either way.
    }
    api.clear()
    buildDefaultDockLayout(api, paneContentRef.current, paneBadgesRef.current)
  }

  return (
    <div
      // `fixed inset-0` — NOT h-full/h-screen/100dvh — is what actually
      // guarantees this room can never hand the document a scrollbar.
      // App.tsx wraps every route in a plain `min-h-screen` div (correct
      // for normal pages, which should grow and let the document scroll)
      // — a percentage/viewport-unit height on this div still lives INSIDE
      // that normal flow, so the instant dockview's measured grid lands even
      // a hair taller than the viewport (a font swap, a sub-pixel rounding
      // step), min-h-screen's div simply grows to fit it and the WHOLE
      // DOCUMENT becomes scrollable — no html/body overflow-hidden override
      // fixes that, because body genuinely IS taller than the viewport at
      // that point. `fixed inset-0` sidesteps the whole flow: this room is
      // pinned to the viewport's four edges independent of any ancestor's
      // height, so it can never make the document taller no matter what
      // dockview measures internally.
      className="fixed inset-0 flex flex-col bg-gray-50 overflow-hidden"
      style={{
        // Each pane's own internal content padding (the workspace's outer/
        // between-panel spacing is now entirely --dv-spacing-padding, on
        // the dockview-theme-light-spaced rule below). Kept deliberately
        // small: this is a dense, space-efficient workspace, not a padded
        // dashboard.
        '--space-pad': '10px',
      } as React.CSSProperties}
    >
      {isAiSession && sessionId && <AiInterviewerControlPanel sessionId={sessionId} />}
      {/* Subtle scrollbars for every `li-scroll` region below (Pane bodies,
          the Interview pane's scrollable half, Chat's message list) — thin
          and low-contrast rather than the OS default, and confined to the
          regions that actually scroll (see this page's "no cut-off /
          correct overflow discipline" pass). Not added to the Tailwind
          config since this page is the only dense-scrolling surface that
          needs it. */}
      <style>{`
        /* Same guarantee as the documentElement/body overflow-hidden effect
           above, but as a STYLESHEET rule rather than an imperative one: a
           <style> tag committed as part of this render is in the DOM (and
           therefore already applied) for this component's very FIRST paint,
           while the JS effect only runs in a useEffect, which fires AFTER
           that first paint. Without this, a genuine — if brief — window
           exists between "this room's first frame is on screen" and "the
           effect has actually run" where dockview's own initial layout
           (five synchronous addPanel calls in buildDefaultDockLayout, each
           able to trigger its own ResizeObserver-driven relayout) could
           still hand the document a real, visible scrollbar for that one
           frame. !important because this rule's whole job is to WIN over
           anything else that might touch html/body overflow; the JS effect
           still exists too, for the correct "restore the exact prior value"
           behavior on unmount that a plain stylesheet rule can't do.
           Removed the instant this component unmounts — this <style> tag is
           React-managed JSX, not a global stylesheet edit. */
        html, body { overflow: hidden !important; }

        .li-scroll { scrollbar-width: thin; scrollbar-color: rgba(0,0,0,0.18) transparent; }
        .li-scroll::-webkit-scrollbar { width: 6px; height: 6px; }
        .li-scroll::-webkit-scrollbar-track { background: transparent; }
        .li-scroll::-webkit-scrollbar-thumb { background-color: rgba(0,0,0,0.18); border-radius: 3px; }
        .li-scroll::-webkit-scrollbar-thumb:hover { background-color: rgba(0,0,0,0.3); }

        /* dockview's built-in "light-spaced" theme already gives every
           section its own gapped, rounded, drop-shadowed card (see
           dockview.css's --spaced variants) — a real step up from the flush,
           browser-tab-strip look of the plain "light" theme this replaced.
           What follows is this page's own brand pass on top of it: purple
           accents in place of the theme's default violet/gray, a shorter
           compact tab strip matching this page's other h-7/h-11 chrome, and
           a card background that matches the page's own canvas exactly
           (gray-50) instead of the theme's slightly-lavender default. */
        .dockview-theme-light-spaced {
          /* Tightened from the base theme's 10px — this is the ONLY source
             of spacing around/between sections (the outer workspace wrapper
             below carries no padding of its own anymore; the two used to
             stack, reading as an oversized gap on every edge). */
          --dv-spacing-padding: 4px;
          --dv-group-view-background-color: #f9fafb;
          --dv-tabs-and-actions-container-height: 32px;
          --dv-tab-font-size: 12.5px;
          --dv-tab-margin-block: 0.4rem;
          --dv-tab-margin-inline: 0.2rem;
          --dv-border-radius: 10px;
          --dv-tab-border-radius: 7px;
          --dv-edge-dock-indicator-color: rgba(147, 51, 234, 0.85);
          --dv-drag-over-border-color: rgba(147, 51, 234, 0.55);
          --dv-drag-over-background-color: rgba(147, 51, 234, 0.08);
          --dv-active-sash-color: #9333ea;
          --dv-activegroup-visiblepanel-tab-background-color: white;
          --dv-activegroup-hiddenpanel-tab-background-color: transparent;
          --dv-inactivegroup-visiblepanel-tab-background-color: white;
          --dv-inactivegroup-hiddenpanel-tab-background-color: transparent;
          --dv-activegroup-visiblepanel-tab-color: #111827;
          --dv-activegroup-hiddenpanel-tab-color: #6b7280;
          --dv-inactivegroup-visiblepanel-tab-color: #374151;
          --dv-inactivegroup-hiddenpanel-tab-color: #9ca3af;
        }
        /* Card elevation dockview's own theme doesn't add on its own — this
           is what makes each section actually read as a raised card sitting
           on the gray-50 canvas, matching the rest of the app's card style
           (see Tooltip/modal shadow-sm conventions elsewhere). */
        .dockview-theme-light-spaced .dv-resize-container:has(> .dv-groupview) {
          box-shadow: 0 1px 2px rgba(0,0,0,0.04), 0 2px 6px rgba(0,0,0,0.06);
        }
        .dockview-theme-light-spaced .dv-groupview {
          border: 1px solid #e5e7eb;
        }
        /* The selected tab reads as a small lifted chip — bold text, a soft
           shadow, and a purple bottom accent — rather than dockview's
           default "background color swap", so the active section is
           unmistakable at a glance across five sections. */
        .dockview-theme-light-spaced .dv-tab.dv-active-tab {
          font-weight: 600;
          box-shadow: 0 1px 2px rgba(0,0,0,0.08), inset 0 -2px 0 0 #9333ea;
        }
        .dockview-theme-light-spaced .dv-tab.dv-inactive-tab:hover {
          background-color: #f3f4f6 !important;
        }
        /* Groupview header background is otherwise white-on-white with the
           tab strip — a hairline separates the two so a single open section
           doesn't look like a borderless slab. */
        .dockview-theme-light-spaced .dv-tabs-and-actions-container {
          border-bottom: 1px solid #f3f4f6;
        }
      `}</style>

      {/* Compact app header (~44px) — brand mark left, connection status +
          Reset Layout right. No page-level scroll, no decorative canvas
          texture behind it: the split-pane workspace below fills virtually
          the entire viewport, so there's no empty backdrop left to texture. */}
      <div className="flex-shrink-0 min-w-0 h-11 flex items-center justify-between gap-3 px-4 bg-white border-b border-gray-200 overflow-hidden">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors flex-shrink-0"
          >
            <ArrowLeftIcon className="w-4 h-4" /> Back
          </button>
          <span className="hidden sm:block w-px h-5 bg-gray-200 flex-shrink-0" />
          <div className="hidden sm:flex items-center gap-2 min-w-0">
            <div className="flex items-center justify-center w-6 h-6 bg-gradient-primary rounded-md flex-shrink-0">
              <Bot className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="text-sm font-semibold text-gradient truncate">AI Recruit</span>
            <span className="text-xs text-gray-500 truncate">Live Interview</span>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {/* A dropped socket is no longer indistinguishable from a slow first
              connect: 'reconnecting'/'disconnected' say which is happening (the
              control bar's wifi glyph carries the same state visually). */}
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <span
              className={`inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                meeting.connectionState === 'failed' || meeting.connectionState === 'disconnected' ? 'bg-red-400'
                  : meeting.connectionState === 'reconnecting' ? 'bg-amber-400 animate-pulse'
                    : meeting.connected ? 'bg-green-400' : 'bg-amber-400 animate-pulse'
              }`}
            />
            {meeting.connectionState === 'reconnecting' ? 'Reconnecting…'
              : meeting.connectionState === 'failed' ? 'Connection lost — leaving…'
                : meeting.connectionState === 'disconnected' ? 'Disconnected — reload to rejoin'
                  : meeting.connected ? 'Connected' : 'Connecting…'}
          </div>
          {/* The launcher — one icon per section, Teams/Slack-sidebar style:
              highlighted purple while that section is open (click to close
              it, same as its tab's own × ), plain gray while closed (click
              to reopen it beside whatever's currently focused). This is the
              "what all can be added back" list closing a section's × alone
              would otherwise have no way to undo. */}
          <div className="hidden md:flex items-center gap-0.5 pr-2 mr-1 border-r border-gray-200">
            {PANE_ORDER.map((id) => {
              const Icon = PANE_ICONS[id]
              const open = openPanelIds.has(id)
              return (
                <Tooltip key={id} content={`${open ? 'Close' : 'Open'} ${PANE_TITLES[id]}`} position="bottom" theme="dark">
                  <button
                    type="button"
                    onClick={() => handleTogglePanel(id)}
                    aria-pressed={open}
                    aria-label={`${open ? 'Close' : 'Open'} ${PANE_TITLES[id]}`}
                    className={`relative inline-flex items-center justify-center w-7 h-7 rounded-md transition-colors ${
                      open ? 'bg-purple-50 text-purple-600' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {id === 'chat' && unreadChat > 0 && !open && (
                      <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-red-500 ring-2 ring-white" />
                    )}
                    {/* Unseen integrity events - shown whether the panel is
                        open or not, since it's usually a background tab. */}
                    {id === 'integrity' && unseenIntegrity > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-amber-500 ring-2 ring-white" />
                    )}
                  </button>
                </Tooltip>
              )
            })}
          </div>
          {/* Escape hatch for the workspace below: clears whatever's been
              dragged/closed and rebuilds the default Video / Interview |
              Live Scoring / Main Notes | Chat arrangement (see
              buildDefaultDockLayout). Touches layout only, never any
              interview/application state. */}
          <Tooltip content="Reset Video, Interview, Live Scoring, Main Notes and Chat back to their default positions and sizes." position="bottom" theme="dark" width="16rem">
            <button
              type="button"
              onClick={handleResetDockLayout}
              title="Reset panel layout"
              aria-label="Reset panel layout"
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors"
            >
              <ArrowsPointingOutIcon className="w-3.5 h-3.5" />
              Reset Layout
            </button>
          </Tooltip>
        </div>
      </div>

      {/* The workspace fills the viewport from just below the header down to
          the bottom edge — see this page's header-comment "LAYOUT" note. No
          padding of its own: the dockview-theme-light-spaced rule above
          already gives every section a consistent gap on every edge (via
          --dv-spacing-padding) — adding padding here too just stacked into a
          double-wide gap around the outer edge specifically.

          ROOT-CAUSED (not just patched) page-level scrollbar fix: this used
          to be a plain flex child (`flex-1 min-h-0 min-w-0 flex
          overflow-hidden`), sized by resolving percentages through several
          nested flex/height layers (NoSidebarLayout's h-screen -> this
          page's own h-full root -> this div's flex-1). dockview-react/
          dockview-core measures ITS OWN container via ResizeObserver
          (getBoundingClientRect, sub-pixel/fractional) and lays its grid out
          in absolute pixels to exactly match that measurement — so any
          fractional slop anywhere in that flex/percentage chain (a
          font-swap reflow, a tab strip acquiring a wider intrinsic width, a
          sub-pixel rounding step between two independent flex resolutions)
          could hand dockview a measured size a hair larger than the true
          remaining space, which it then faithfully re-renders as its own
          panel-group pixel dimensions — enough to trip a browser scrollbar
          without ever being visually obvious. The html/body
          `overflow: hidden` rules elsewhere on this page are a real belt
          alongside this, but they don't stop that inflated size from being
          computed and painted in the first place.

          Fixed here instead: `fixed inset-x-0 top-11` takes this div OUT of
          the flex/percentage resolution chain entirely and pins it directly
          to the viewport — top offset hard-pinned to the exact same
          Tailwind token (`top-11` = 2.75rem = 44px) as the header's own
          `h-11`, which is itself a fixed, non-negotiable height (explicit
          height + overflow-hidden on that header div, so it can never grow
          past 44px regardless of its content).

          TWO ROUNDS of this fix have both left the product owner still
          seeing a page-level scrollbar on the right and bottom edges — so
          rather than chase why dockview's own ResizeObserver measurement is
          still landing a hair larger than this box (a third round of pure
          measurement debugging), this makes overflow past this box
          PHYSICALLY IMPOSSIBLE instead of merely unlikely: inline
          `maxWidth`/`maxHeight` (not just the Tailwind classes above, so
          nothing else can out-precedence them) hard-cap this container's
          own dimensions to the viewport regardless of what `top`/`bottom`
          resolve to, and `overflow: 'hidden'` on that capped box means
          anything dockview renders past its edges — even if dockview
          internally still believes it has more room than it actually does —
          gets CROPPED by this container instead of ever reaching a point
          where the browser considers the document itself scrollable. The
          product owner has explicitly said panels rendering slightly
          smaller/clipped is fine; a page-level scrollbar is not.
          BOTTOM_RESERVE_PX (see its own const comment) is subtracted from
          both `bottom` and the `maxHeight` calc together so this box's
          actual bottom edge sits a few pixels above the true viewport
          bottom — a deliberate reserved sliver for
          AutoHidingPinnedControlBar's hover hotzone (see MeetingStage's
          `AutoHidingPinnedControlBar`), so that always-present hotzone strip
          and this workspace's own bottom-anchored content (Chat's message
          input, Main Notes' textarea) never occupy the same pixels — without
          this gap the hotzone's `pointer-events: auto` sat exactly on top of
          the chat input and silently ate every click meant for it. Safe to
          escape normal flow this way because nothing between this div and
          <body> (NoSidebarLayout's root, this page's own root) sets
          transform/filter/perspective/contain, any of which would turn
          `fixed` into "relative to that ancestor" instead of the viewport —
          verified clean. */}
      {/* FOUND THE ACTUAL ROOT CAUSE (verified with a real logged-in Playwright
          inspection of this exact page, not just theorizing): the dockview
          grid was rendering exactly `--dv-spacing-padding` (4px) too wide.
          `dockview-theme-light-spaced`'s own base rule sets `padding` +
          `box-sizing: border-box` directly on whatever element carries that
          class — and that class was on <DockviewReact> itself, i.e. the
          EXACT div dockview-react measures via `clientWidth`/`clientHeight`
          to lay its grid out in absolute pixels. `clientWidth` includes
          padding (it's the padding-box size), so dockview read the FULL
          width (e.g. 1600px) as "space available for my grid" — then placed
          that grid's children starting at the padding-box origin (inset by
          the padding, e.g. x=4) and sized to that full measured width, which
          lands its right/bottom edge exactly `padding` px past the element's
          true outer edge. Self-inflicted: the theme's own gap styling and
          dockview's own self-measurement were fighting over the same box.
          Fix: give the THEME class (padding + CSS variables) to THIS
          wrapper div instead — CSS custom properties and the
          `.dockview-theme-light-spaced .dv-xxx` descendant rules still reach
          DockviewReact's internals fine from an ancestor — and let
          DockviewReact's own root stay padding-free, sized via plain
          `height:100%;width:100%` of this wrapper's (already
          padding-shrunk) CONTENT box. Now there is nothing left for it to
          measure that includes padding it doesn't know to subtract, so the
          grid it computes actually fits — the maxWidth/maxHeight/
          overflow-hidden clipping below is kept as a defensive backstop, not
          load-bearing anymore. */}
      <div
        className="fixed inset-x-0 top-11 flex overflow-hidden dockview-theme-light-spaced"
        style={{
          bottom: BOTTOM_RESERVE_PX,
          maxWidth: '100vw',
          maxHeight: `calc(100dvh - 44px - ${BOTTOM_RESERVE_PX}px)`,
          overflow: 'hidden',
        }}
      >
        <DockviewReact
          className="h-full w-full"
          // Explicit `theme` (not just a `className` string) is load-bearing:
          // dockview-core defaults to its OWN dark `themeAbyss` whenever no
          // `theme` option is passed, and applies that default's className
          // internally to its own "shell" elements via _shellThemeClassnames
          // — regardless of whatever className WE hand DockviewReact. That
          // default class sits closer to elements like the tab strip than
          // our own wrapper div's className, so its dark
          // --dv-tabs-and-actions-container-background-color (etc.) won by
          // proximity, which is why tabs/borders were rendering black.
          // Passing the real themeLightSpaced preset here is what actually
          // tells dockview-core to use light-spaced instead of silently
          // defaulting to abyss.
          theme={themeLightSpaced}
          components={DOCK_COMPONENTS}
          defaultTabComponent={WorkspacePanelTab}
          watermarkComponent={WorkspaceWatermark}
          onReady={handleDockReady}
        />
      </div>

      {tourStep !== null && (
        <RecruiterInterviewTour
          step={tourStep}
          onSkip={() => setTourStep(null)}
          onNext={() => setTourStep((s) => (s === null ? null : s + 1))}
          onFinish={() => setTourStep(null)}
        />
      )}
    </div>
  )
}

// ============================================================================
// Guided tour — same mechanism as Proctored Interview's InterviewTour
// (PublicInterviewPage.tsx) and Live Interview's own candidate-side
// LiveInterviewTour (LiveInterviewRoomPage.tsx): shown once, right when this
// recruiter connects. Step 0 is a centered intro card (Skip / Take the
// tour); steps 1..RECRUITER_TOUR_STEPS.length spotlight one dockview tab
// header each, via the `data-tour="dock-tab-*"` attribute WorkspaceDockview's
// WorkspacePanelTab now carries (see its own comment for why a tab HEADER,
// not its content pane, is the anchor). Duplicated locally rather than
// imported from either of those other two files — none of the three exports
// its tour, and all three candidate/recruiter flows are otherwise kept
// independent. The spotlight itself is a single absolutely-positioned box
// with a giant box-shadow (a common no-library "hole in the overlay" trick)
// rather than an SVG mask.
// ============================================================================

const RECRUITER_TOUR_STEPS: { selector: string; title: string; description: string }[] = [
  { selector: '[data-tour="dock-tab-video"]', title: 'Video Call', description: 'Your live video with the candidate. Drag any tab in this workspace onto another panel to rearrange things your way.' },
  { selector: '[data-tour="control-bar"]', title: 'Your Controls', description: 'Mic, camera, screen share, session recording, and Leave/End Interview — pinned to the bottom of the screen. Hover the bottom edge any time to bring them back up.' },
  { selector: '[data-tour="dock-tab-interview"]', title: 'Interview', description: 'Generate and reveal questions to the candidate here, and track their answers as they come in.' },
  { selector: '[data-tour="dock-tab-live-scoring"]', title: 'Live Scoring', description: "Tabbed together with Interview by default — see the candidate's running scores and ability estimate update as they answer." },
  { selector: '[data-tour="dock-tab-main-notes"]', title: 'Notes', description: 'Jot down notes during the call — saved automatically, and they carry over to the interview summary afterward.' },
  { selector: '[data-tour="dock-tab-chat"]', title: 'Chat', description: 'Send the candidate a text message any time without interrupting the call.' },
]

function RecruiterInterviewTour({
  step, onSkip, onNext, onFinish,
}: { step: number; onSkip: () => void; onNext: () => void; onFinish: () => void }) {
  const [rect, setRect] = useState<DOMRect | null>(null)
  const spotlightIndex = step - 1 // step 0 is the intro card, which has no target

  useEffect(() => {
    const current = RECRUITER_TOUR_STEPS[spotlightIndex]
    if (!current) {
      setRect(null)
      return
    }
    const el = document.querySelector(current.selector)
    if (!el) {
      setRect(null)
      return
    }
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    const measure = () => setRect(el.getBoundingClientRect())
    measure()
    // Re-measure a few times while the smooth scroll above is still animating.
    let ticks = 0
    const interval = setInterval(() => {
      measure()
      if (++ticks > 6) clearInterval(interval)
    }, 100)
    window.addEventListener('resize', measure)
    return () => {
      clearInterval(interval)
      window.removeEventListener('resize', measure)
    }
  }, [spotlightIndex])

  if (step === 0) {
    return (
      <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 text-center">
          <h2 className="text-lg font-semibold text-gray-900 mb-1.5">Want a quick tour?</h2>
          <p className="text-sm text-gray-500 mb-5">We'll walk you through your interview workspace in a few seconds.</p>
          <div className="flex items-center gap-2">
            <button onClick={onSkip} className="flex-1 px-4 py-2.5 bg-white text-gray-600 border border-gray-300 rounded-xl text-sm font-semibold hover:bg-gray-50 transition-colors">
              Skip
            </button>
            <button onClick={onNext} className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors">
              Take the tour
            </button>
          </div>
        </div>
      </div>
    )
  }

  const current = RECRUITER_TOUR_STEPS[spotlightIndex]
  if (!current || !rect) return null
  const isLast = spotlightIndex === RECRUITER_TOUR_STEPS.length - 1

  // Neither "above" nor "below" is guaranteed to have room: a spotlighted
  // element can span nearly the full viewport height (e.g. the Video tab's
  // own content), leaving no space on either side. `placeAbove`/`placeBelow`
  // each require real clearance (170px, a rough card-height budget) before
  // being chosen; when NEITHER fits, the card anchors near the bottom of the
  // viewport instead of computing a `top`/`bottom` from the target's own
  // (off-screen) edge — which previously placed it below the visible
  // viewport entirely, with no way to scroll to it.
  const spaceBelow = window.innerHeight - rect.bottom
  const spaceAbove = rect.top
  const placeAbove = spaceBelow < 170 && spaceAbove >= 170
  const placeBelow = spaceBelow >= 170
  const left = Math.min(Math.max(12, rect.left), window.innerWidth - 300)

  return (
    <>
      {/* Blocks interaction with the page underneath while the tour is active */}
      <div className="fixed inset-0 z-[70]" />
      <div
        className="fixed z-[71] rounded-lg pointer-events-none transition-all duration-200"
        style={{
          top: rect.top - 4,
          left: rect.left - 4,
          width: rect.width + 8,
          height: rect.height + 8,
          boxShadow: '0 0 0 9999px rgba(0,0,0,0.6)',
          border: '2px solid #2563EB',
        }}
      />
      <div
        className="fixed z-[72] bg-white rounded-xl shadow-xl p-4 w-72 transition-all duration-200"
        style={
          placeAbove ? { bottom: window.innerHeight - rect.top + 12, left }
            : placeBelow ? { top: rect.bottom + 12, left }
              : { bottom: 12, left }
        }
      >
        <p className="text-xs font-medium text-blue-600 mb-1">Step {step} of {RECRUITER_TOUR_STEPS.length}</p>
        <p className="text-sm font-semibold text-gray-900 mb-1">{current.title}</p>
        <p className="text-xs text-gray-500 mb-4 leading-relaxed">{current.description}</p>
        <div className="flex items-center justify-between">
          <button onClick={onSkip} className="text-xs font-medium text-gray-400 hover:text-gray-600">Skip tour</button>
          <button
            onClick={isLast ? onFinish : onNext}
            className="px-3.5 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 transition-colors"
          >
            {isLast ? 'Got it' : 'Next'}
          </button>
        </div>
      </div>
    </>
  )
}
